import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar, Users, BookOpen, Award, Clock, User, CheckCircle, XCircle, Plus, FileText, Trash2, Upload, Download, Edit, Eye, X, Check, Link, Filter, Bell, RefreshCw, Menu, ChevronDown, AlertTriangle } from 'lucide-react';
import AthleteBooklet from './AthleteBooklet';
import StarRating from './StarRating';
import UserProfile from './UserProfile';
import MultiTeacherSelect from './MultiTeacherSelect';
import EventEnrollments from './EventEnrollments';
import CompetitionAnalysis from './CompetitionAnalysis';
import AreaFiltro from './AreaFiltro';
import MaestroAthleteEnrollments from './MaestroAthleteEnrollments';
import { PushNotificationToggle, AdminPushNotifications } from './PushNotifications';
import { toast } from 'sonner';
import DashboardContext from './dashboard/DashboardContext';
import { AtletaDashboard } from './dashboard/AtletaDashboard';
import { TeacherDashboard } from './dashboard/TeacherDashboard';
import { FormatoreDashboard } from './dashboard/FormatoreDashboard';
import { MaestroDashboard } from './dashboard/MaestroDashboard';
import { ContabileDashboard } from './dashboard/ContabileDashboard';
import { AdminDashboard } from './dashboard/AdminDashboard';
import LoadingProgress from './LoadingProgress';
import { useSessionManager } from '../hooks/useSessionManager';
import SessionWarningDialog from './SessionWarningDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, activeRole, logout } = useAuth();
  
  // Session management
  const { showWarning, timeRemaining, renewSession, logout: sessionLogout } = useSessionManager(user, logout);
  
  // Loading progress state
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [isLoadingData, setIsLoadingData] = useState(false);
  
  // Helper function to get current role with fallback
  const getCurrentRole = () => {
    return activeRole || (user?.roles && user.roles[0]) || user?.role || 'atleta';
  };

  // Check athlete profile completeness when user changes
  useEffect(() => {
    if (user) {
      checkAthleteProfileComplete(user);
      checkMaestroProfileComplete(user);
      // Pre-populate mandatory profile data with existing values
      setMandatoryProfileData({
        birth_date: user.birth_date || '',
        fiscal_code: user.fiscal_code || '',
        athlete_class: user.athlete_class || '',
        asa_reference: user.asa_reference || '',
        phone: user.phone || '',
        address: user.address || '',
        region: user.region || '',
        disciplines: user.disciplines || [],
        disciplines_other: user.disciplines_other || ''
      });
      setMaestroProfileData({
        school_name: user.school_name || '',
        school_address: user.school_address || '',
        school_phone: user.school_phone || '',
        school_email: user.school_email || '',
        school_vat: user.school_vat || ''
      });
    }
  }, [user]);
  const [myEvents, setMyEvents] = useState([]);
  const [matchAnalyses, setMatchAnalyses] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Mobile menu state for athlete/teacher tabs
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeAthleteTab, setActiveAthleteTab] = useState('events');
  const [activeTeacherTab, setActiveTeacherTab] = useState('competition');
  const [teacherMobileMenuOpen, setTeacherMobileMenuOpen] = useState(false);
  const [activeFormatoreTab, setActiveFormatoreTab] = useState('competition');
  const [formatoreMobileMenuOpen, setFormatoreMobileMenuOpen] = useState(false);
  const [activeAdminTab, setActiveAdminTab] = useState('users');
  const [adminMobileMenuOpen, setAdminMobileMenuOpen] = useState(false);
  const [activeMaestroTab, setActiveMaestroTab] = useState('athletes');
  const [maestroMobileMenuOpen, setMaestroMobileMenuOpen] = useState(false);
  
  // Maestro-specific states
  const [maestroAthletes, setMaestroAthletes] = useState([]);
  const [maestroEvents, setMaestroEvents] = useState([]);
  const [selectedAthletesForEnrollment, setSelectedAthletesForEnrollment] = useState([]);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [selectedEventForEnrollment, setSelectedEventForEnrollment] = useState(null);
  const [showCreateAthleteModal, setShowCreateAthleteModal] = useState(false);
  const [showCreateDetailedAnalysis, setShowCreateDetailedAnalysis] = useState(false);
  const [newAthleteData, setNewAthleteData] = useState({
    email: '',
    password: '',
    name: '',
    surname: '',
    payment_pdf: null
  });
  const [showDuplicateAthleteDialog, setShowDuplicateAthleteDialog] = useState(false);
  const [duplicateAthleteInfo, setDuplicateAthleteInfo] = useState(null);
  const [athletesCreatedByMaestros, setAthletesCreatedByMaestros] = useState([]);
  const [maestrosForFilter, setMaestrosForFilter] = useState([]);
  const [selectedMaestroFilter, setSelectedMaestroFilter] = useState('all');
  const [showDeletionRequestModal, setShowDeletionRequestModal] = useState(false);
  const [selectedEnrollmentForDeletion, setSelectedEnrollmentForDeletion] = useState(null);
  const [deletionReason, setDeletionReason] = useState('');
  const [enrollmentDeletionRequests, setEnrollmentDeletionRequests] = useState([]);
  
  // Collapsed sections for approved users by role
  const [collapsedRoles, setCollapsedRoles] = useState({
    admin: true,
    teacher: true,
    formatore: true,
    maestro: true,
    contabile: true,
    atleta: true
  });

  // Form states
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    type: 'stage',
    date_tbd: false,
    start_date: '',
    end_date: '',
    teacher_ids: [],
    external_teachers_text: '',
    location: '',
    max_participants: 20,
    poster_url: '',
    dance_analysis: false
  });
  
  const [posterFile, setPosterFile] = useState(null);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  
  const [newMatchAnalysis, setNewMatchAnalysis] = useState({
    event_id: '',
    athlete_id: '',
    content: '',
    is_private_lesson: false,
    athlete_name: '',
    athlete_surname: ''
  });
  
  const [newCertificate, setNewCertificate] = useState({
    user_id: '',
    title: '',
    description: ''
  });

  const [newUser, setNewUser] = useState({
    name: '',
    surname: '',
    email: '',
    password: '',
    role: 'atleta',
    roles: ['atleta']
  });

  const [detailedMatchAnalyses, setDetailedMatchAnalyses] = useState([]);
  const [newDetailedAnalysis, setNewDetailedAnalysis] = useState({
    event_id: '',
    athlete_id: '',
    is_private_lesson: false,
    athlete_name: '',
    athlete_surname: '',
    scores: {
      tempo: 5,
      ritmo: 5,
      posizione_piedi: 5,
      equilibrio: 5,
      correttezza_eleganza: 5,
      spinta_slancio: 5,
      rotazione_testa: 5,
      eleganza_estetica: 5,
      fluidita_agilita: 5,
      espressivita_carattere: 5,
      musicalita_interpretazione: 5,
      stile_portamento: 5
    },
    notes: ''
  });

  const [certificateFile, setCertificateFile] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editEventData, setEditEventData] = useState({});
  const [editPosterFile, setEditPosterFile] = useState(null); // Per modifica locandina
  const [selectedEventEnrollments, setSelectedEventEnrollments] = useState(null);
  const [generatingCertificates, setGeneratingCertificates] = useState(null); // Event ID being processed
  const [regeneratingCertificates, setRegeneratingCertificates] = useState(null); // Event ID being regenerated
  const [matchResultForm, setMatchResultForm] = useState({ event_id: '', athlete_id: '', athlete_search: '', content: 'Vedi Certificato allegato.', result_score: '', judgments_advice: 'Vedi Certificato allegato.', certificate_pdf_data: null, certificate_pdf_filename: null });
  const [adminMatchAnalyses, setAdminMatchAnalyses] = useState([]);
  const [rejectedEnrollments, setRejectedEnrollments] = useState([]);
  const [associateAthleteSearch, setAssociateAthleteSearch] = useState('');
  const [allAvailableAthletes, setAllAvailableAthletes] = useState([]);
  const [transferConflict, setTransferConflict] = useState(null);
  const [eventCertificatesStatus, setEventCertificatesStatus] = useState({}); // {eventId: {total, withCert, withoutCert}}
  
  // Match Analysis modal and filters
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [selectedDetailedAnalysis, setSelectedDetailedAnalysis] = useState(null);
  const [analysisFilters, setAnalysisFilters] = useState({
    date: '',
    event: '',
    athlete: ''
  });

  // Admin states
  const [allMatchAnalyses, setAllMatchAnalyses] = useState([]);
  const [allDetailedMatchAnalyses, setAllDetailedMatchAnalyses] = useState([]);
  const [allCertificates, setAllCertificates] = useState([]);
  const [certificateFilters, setCertificateFilters] = useState({
    event: '',
    athlete: ''
  });
  
  // User list filters and sorting
  const [userFilter, setUserFilter] = useState('all'); // all, admin, teacher, formatore, atleta, pending
  const [userSortOrder, setUserSortOrder] = useState('default'); // default, alphabetical

  // Teacher events states
  const [teacherEvents, setTeacherEvents] = useState([]);
  const [selectedEventEnrollmentsTeacher, setSelectedEventEnrollmentsTeacher] = useState(null);

  // Study materials states
  const [studyMaterials, setStudyMaterials] = useState([]);

  // Enrollment management states
  const [pendingEnrollments, setPendingEnrollments] = useState([]);
  const [enrollmentsCount, setEnrollmentsCount] = useState({
    pending: 0,
    approved: 0,
    rejected: 0
  });
  const [usersCount, setUsersCount] = useState({
    total: 0,
    admin: 0,
    teacher: 0,
    formatore: 0,
    maestro: 0,
    contabile: 0,
    atleta: 0,
    pending_approval: 0
  });
  const [inconsistentUsers, setInconsistentUsers] = useState([]);
  const [selectedInconsistentUsers, setSelectedInconsistentUsers] = useState([]);
  const [fixingInconsistent, setFixingInconsistent] = useState(false);
  const [areaFiltroCount, setAreaFiltroCount] = useState(0);
  const [myEnrollments, setMyEnrollments] = useState([]);
  const [formatori, setFormatori] = useState([]);
  const [selectedEventForMaterials, setSelectedEventForMaterials] = useState(null);
  const [materialUploadForm, setMaterialUploadForm] = useState({
    title: '',
    description: '',
    file: null
  });
  const [selectedEventForLinks, setSelectedEventForLinks] = useState(null);
  const [onlineMeetingLinks, setOnlineMeetingLinks] = useState({
    online_meeting_link_1: '',
    online_meeting_datetime_1: '',
    online_meeting_link_2: '',
    online_meeting_datetime_2: '',
    online_meeting_link_3: '',
    online_meeting_datetime_3: '',
    online_meeting_link_4: '',
    online_meeting_datetime_4: ''
  });
  const [selectedEnrollmentDetails, setSelectedEnrollmentDetails] = useState(null);
  const [enrollmentStudyMaterials, setEnrollmentStudyMaterials] = useState([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isAthleteProfileComplete, setIsAthleteProfileComplete] = useState(true);
  const [showMandatoryProfileModal, setShowMandatoryProfileModal] = useState(false);
  const [showMaestroProfileModal, setShowMaestroProfileModal] = useState(false);
  const [maestroProfileData, setMaestroProfileData] = useState({
    school_name: '', school_address: '', school_phone: '', school_email: '', school_vat: ''
  });
  const [mandatoryProfileData, setMandatoryProfileData] = useState({
    birth_date: '',
    fiscal_code: '',
    athlete_class: '',
    asa_reference: '',
    phone: '',
    address: '',
    region: '',
    disciplines: [],
    disciplines_other: ''
  });
  const [savingMandatoryProfile, setSavingMandatoryProfile] = useState(false);
  const [incompleteProfiles, setIncompleteProfiles] = useState([]);
  const [sendingReminder, setSendingReminder] = useState({});

  // Password reset state
  const [passwordResetModal, setPasswordResetModal] = useState({
    isOpen: false,
    userId: null,
    userEmail: '',
    newPassword: ''
  });

  // Email confirmation modal state
  const [emailModal, setEmailModal] = useState({
    isOpen: false,
    analysisId: null,
    athleteName: '',
    athleteEmail: ''
  });

  // Search filters state
  const [searchFilters, setSearchFilters] = useState({
    name: '',
    surname: '',
    email: '',
    fiscal_code: '',
    asa_reference: '',
    maestro_id: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Helper function for API calls with retry
  const apiCallWithRetry = async (apiCall, maxRetries = 2) => {
    for (let i = 0; i <= maxRetries; i++) {
      try {
        return await apiCall();
      } catch (error) {
        if (i === maxRetries) throw error;
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      }
    }
  };

  // Technical credits state
  const [technicalCreditsModal, setTechnicalCreditsModal] = useState({
    isOpen: false,
    athleteId: '',
    athleteName: '',
    credits: '',
    academicYear: '2025/2026',
    notes: ''
  });

  // Removed external teachers functions - now using simple text field

  useEffect(() => {
    if (user) {
      // Use activeRole if available, otherwise fallback to user.role or first role from user.roles
      const currentRole = activeRole || (user.roles && user.roles[0]) || user.role || 'atleta';
      if (currentRole) {
        fetchDashboardData();
      }
    }
  }, [user, activeRole]);

  // Enrollment management functions
  const fetchPendingEnrollments = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-enrollments`);
      setPendingEnrollments(response.data);
    } catch (error) {
      console.error('Error fetching pending enrollments:', error);
      toast.error('Errore nel caricamento delle iscrizioni pendenti');
    }
  };

  const fetchEnrollmentsCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/enrollments-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEnrollmentsCount(response.data);
    } catch (error) {
      console.error('Error fetching enrollments count:', error);
    }
  };

  const fetchUsersCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUsersCount(response.data);
    } catch (error) {
      console.error('Error fetching users count:', error);
    }
  };

  const fetchInconsistentUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users/inconsistent`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setInconsistentUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching inconsistent users:', error);
    }
  };

  const fixSelectedInconsistentUsers = async () => {
    if (selectedInconsistentUsers.length === 0) {
      toast.error('Seleziona almeno un utente da correggere');
      return;
    }
    
    setFixingInconsistent(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/users/fix-inconsistent`, 
        selectedInconsistentUsers,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setSelectedInconsistentUsers([]);
      fetchInconsistentUsers();
      fetchUsersCount();
      fetchUsers();
    } catch (error) {
      console.error('Error fixing users:', error);
      toast.error('Errore nella correzione degli utenti');
    } finally {
      setFixingInconsistent(false);
    }
  };

  const fixAllInconsistentUsers = async () => {
    if (inconsistentUsers.length === 0) {
      toast.info('Nessun utente da correggere');
      return;
    }
    
    if (!window.confirm(`Vuoi correggere tutti i ${inconsistentUsers.length} utenti con status inconsistente?`)) {
      return;
    }
    
    setFixingInconsistent(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/users/fix-all-inconsistent`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setSelectedInconsistentUsers([]);
      fetchInconsistentUsers();
      fetchUsersCount();
      fetchUsers();
    } catch (error) {
      console.error('Error fixing all users:', error);
      toast.error('Errore nella correzione degli utenti');
    } finally {
      setFixingInconsistent(false);
    }
  };

  const toggleInconsistentUserSelection = (userId) => {
    setSelectedInconsistentUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllInconsistentUsers = () => {
    if (selectedInconsistentUsers.length === inconsistentUsers.length) {
      setSelectedInconsistentUsers([]);
    } else {
      setSelectedInconsistentUsers(inconsistentUsers.map(u => u.id));
    }
  };


  const fetchIncompleteProfiles = async () => {
    try {
      const response = await axios.get(`${API}/admin/incomplete-profiles`);
      setIncompleteProfiles(response.data);
    } catch (error) {
      console.error('Error fetching incomplete profiles:', error);
    }
  };

  const sendProfileReminder = async (userId, userName) => {
    setSendingReminder(prev => ({...prev, [userId]: true}));
    try {
      const response = await axios.post(`${API}/admin/send-profile-reminder/${userId}`);
      toast.success(response.data.message);
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nell\'invio del sollecito';
      toast.error(message);
    } finally {
      setSendingReminder(prev => ({...prev, [userId]: false}));
    }
  };

  const [sendingPaymentReminder, setSendingPaymentReminder] = useState({});

  const sendPaymentReminder = async (enrollmentId) => {
    setSendingPaymentReminder(prev => ({...prev, [enrollmentId]: true}));
    try {
      const response = await axios.post(`${API}/admin/enrollments/${enrollmentId}/payment-reminder`);
      toast.success(response.data.message);
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nell\'invio del sollecito';
      toast.error(message);
    } finally {
      setSendingPaymentReminder(prev => ({...prev, [enrollmentId]: false}));
    }
  };

  const [sendingApprovalReminder, setSendingApprovalReminder] = useState({});

  const sendApprovalReminder = async (userId) => {
    setSendingApprovalReminder(prev => ({...prev, [userId]: true}));
    try {
      const response = await axios.post(`${API}/admin/users/${userId}/approval-reminder`);
      toast.success(response.data.message);
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nell\'invio del sollecito';
      toast.error(message);
    } finally {
      setSendingApprovalReminder(prev => ({...prev, [userId]: false}));
    }
  };



  const fetchAreaFiltroCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/area-filtro/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAreaFiltroCount(response.data.count);
    } catch (error) {
      console.error('Error fetching area filtro count:', error);
    }
  };

  const approveEnrollment = async (enrollmentId) => {
    // This is now just for opening the approval modal
    const enrollment = pendingEnrollments.find(e => e.id === enrollmentId);
    if (enrollment) {
      setApprovalModal({
        enrollment,
        type: 'free',
        amount: '',
        amount_cibs: '',
        amount_midas: ''
      });
    }
  };

  const handleApprovalConfirm = async () => {
    if (!approvalModal) return;
    
    const { enrollment, type, amount, amount_cibs, amount_midas } = approvalModal;
    
    // Validate paid approval has amount
    if (type === 'paid' && (!amount || parseFloat(amount) <= 0)) {
      toast.error('Inserisci un importo valido per l\'approvazione a pagamento');
      return;
    }

    // Validate mixed approval: at least one of the two amounts must be > 0; the other can be left empty (treated as 0 = "Gratuito").
    if (type === 'mixed') {
      const cibs = amount_cibs ? parseFloat(amount_cibs) : 0;
      const midas = amount_midas ? parseFloat(amount_midas) : 0;
      if (cibs < 0 || midas < 0) {
        toast.error('Le quote non possono essere negative');
        return;
      }
      if (cibs <= 0 && midas <= 0) {
        toast.error('Inserisci almeno una delle due quote (CIBS o MIDAS). Per iscrizione totalmente gratuita usa "Approva Gratuitamente".');
        return;
      }
    }
    
    try {
      const payload = { approval_type: type };
      if (type === 'paid') {
        payload.amount = parseFloat(amount);
      } else if (type === 'mixed') {
        payload.amount_cibs = amount_cibs ? parseFloat(amount_cibs) : 0;
        payload.amount_midas = amount_midas ? parseFloat(amount_midas) : 0;
      }

      const response = await axios.patch(`${API}/admin/enrollments/${enrollment.id}/approve`, payload);
      
      if (type === 'free') {
        toast.success('Iscrizione approvata gratuitamente');
      } else if (type === 'mixed') {
        toast.success(`Email di richiesta pagamento misto inviata a ${response.data.email_sent_to}`);
      } else {
        toast.success(`Email di richiesta pagamento inviata a ${response.data.email_sent_to}`);
      }
      
      setApprovalModal(null);
      fetchPendingEnrollments();
      fetchPendingPaymentEnrollments();
      fetchEnrollmentsCount();
      fetchEvents();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nell\'approvazione dell\'iscrizione';
      toast.error(message);
    }
  };

  const fetchPendingPaymentEnrollments = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-payment-enrollments`);
      setPendingPaymentEnrollments(response.data);
    } catch (error) {
      console.error('Error fetching pending payment enrollments:', error);
    }
  };

  const confirmPaymentReceived = async (enrollmentId) => {
    if (!window.confirm('Confermi di aver ricevuto il pagamento per questa iscrizione?')) return;
    
    try {
      await axios.patch(`${API}/admin/enrollments/${enrollmentId}/confirm-payment`);
      toast.success('Pagamento confermato e iscrizione approvata');
      fetchPendingPaymentEnrollments();
      fetchEnrollmentsCount();
      fetchEvents();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nella conferma del pagamento';
      toast.error(message);
    }
  };

  const approveEnrollmentUnpaid = async (enrollmentId) => {
    if (!window.confirm('Inserire questo atleta negli iscritti all\'evento come NON PAGATO?')) return;
    try {
      const response = await axios.patch(`${API}/admin/enrollments/${enrollmentId}/approve-unpaid`);
      toast.success(response.data.message);
      fetchPendingPaymentEnrollments();
      fetchEnrollmentsCount();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };


  const rejectEnrollment = async (enrollmentId) => {
    if (window.confirm('Sei sicuro di voler rifiutare questa iscrizione?')) {
      try {
        await axios.patch(`${API}/admin/enrollments/${enrollmentId}/reject`);
        toast.success('Iscrizione rifiutata');
        fetchPendingEnrollments();
        fetchPendingPaymentEnrollments();
        fetchEnrollmentsCount();
      } catch (error) {
        const message = error.response?.data?.detail || 'Errore nel rifiuto dell\'iscrizione';
        toast.error(message);
      }
    }
  };

  // Payment management states
  const [paymentModal, setPaymentModal] = useState(null); // {enrollmentId, currentPaid}
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');

  // Enrollment approval modal states
  const [approvalModal, setApprovalModal] = useState(null); // {enrollment: {...}, type: 'free'|'paid'|'mixed', amount: number, amount_cibs: '', amount_midas: ''}
  const [pendingPaymentEnrollments, setPendingPaymentEnrollments] = useState([]);

  const updatePaymentStatus = async (enrollmentId, paid, paymentMethod = null) => {
    try {
      const params = new URLSearchParams();
      params.append('paid', paid);
      if (paid && paymentMethod) {
        params.append('payment_method', paymentMethod);
      }
      
      await axios.patch(`${API}/admin/enrollments/${enrollmentId}/payment?${params.toString()}`);
      toast.success(paid ? 'Pagamento confermato' : 'Pagamento annullato');
      
      // Refresh event enrollments if modal is open
      if (selectedEventEnrollments) {
        const response = await axios.get(`${API}/admin/events/${selectedEventEnrollments.id}/enrollments`);
        setSelectedEventEnrollments({
          ...selectedEventEnrollments,
          enrollments: response.data
        });
      }
      
      setPaymentModal(null);
      setSelectedPaymentMethod('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nell\'aggiornamento del pagamento';
      toast.error(message);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setIsLoadingData(true);
      setLoadingProgress(0);
      
      const currentRole = activeRole || (user.roles && user.roles[0]) || user.role || 'atleta';
      let totalSteps = 5; // Default
      
      // Helper to safely execute fetch functions without breaking Promise.all
      const safeFetch = async (fetchFn, name) => {
        try {
          await fetchFn();
        } catch (error) {
          console.error(`Error in ${name}:`, error);
          // Don't throw - let other fetches continue
        }
      };

      // Progress tracking helper
      let completedSteps = 0;
      const trackProgress = async (fetchFn, name, stepWeight = 1) => {
        await safeFetch(fetchFn, name);
        completedSteps += stepWeight;
        const progressPercent = Math.min(Math.round((completedSteps / totalSteps) * 100), 100);
        setLoadingProgress(progressPercent);
      };
      
      if (currentRole === 'atleta') {
        setLoadingMessage('Caricamento dati atleta...');
        // Parallel fetch for athlete data
        await Promise.all([
          trackProgress(fetchMyEvents, 'fetchMyEvents'),
          trackProgress(fetchMyEnrollments, 'fetchMyEnrollments'),
          trackProgress(fetchMyMatchAnalyses, 'fetchMyMatchAnalyses'),
          trackProgress(fetchMyCertificates, 'fetchMyCertificates'),
          trackProgress(fetchDetailedMatchAnalyses, 'fetchDetailedMatchAnalyses')
        ]);
      } else if (currentRole === 'teacher') {
        setLoadingMessage('Caricamento dati insegnante...');
        // Parallel fetch for teacher data
        await Promise.all([
          trackProgress(fetchEvents, 'fetchEvents'),
          trackProgress(fetchMyEnrollments, 'fetchMyEnrollments'),
          trackProgress(fetchTeacherMatchAnalyses, 'fetchTeacherMatchAnalyses'),
          trackProgress(fetchUsers, 'fetchUsers'),
          trackProgress(fetchDetailedMatchAnalyses, 'fetchDetailedMatchAnalyses'),
          trackProgress(fetchTeacherEvents, 'fetchTeacherEvents')
        ]);
      } else if (currentRole === 'formatore') {
        setLoadingMessage('Caricamento eventi...');
        await Promise.all([
          trackProgress(fetchEvents, 'fetchEvents'),
          trackProgress(fetchFormatoreEvents, 'fetchFormatoreEvents')
        ]);
      } else if (currentRole === 'admin') {
        // LAZY LOADING for Admin: Load essential data first, then secondary data
        const totalSteps = 6; // Only count essential steps for progress
        
        // Phase 1: Essential data in parallel
        setLoadingMessage('Caricamento dati admin...');
        await Promise.all([
          trackProgress(fetchUsers, 'fetchUsers'),
          trackProgress(fetchEvents, 'fetchEvents'),
          trackProgress(fetchUsersCount, 'fetchUsersCount'),
          trackProgress(fetchEnrollmentsCount, 'fetchEnrollmentsCount'),
          trackProgress(fetchPendingEnrollments, 'fetchPendingEnrollments'),
          trackProgress(fetchRejectedEnrollments, 'fetchRejectedEnrollments'),
          trackProgress(fetchAreaFiltroCount, 'fetchAreaFiltroCount')
        ]);
        
        // Phase 2: Secondary data (loaded in background after UI is ready)
        setLoadingMessage('Finalizzazione...');
        setLoadingProgress(100);
        
        // Load these in background without blocking UI - staggered to avoid overwhelming server
        setTimeout(() => {
          safeFetch(fetchTeachers, 'fetchTeachers');
          safeFetch(fetchFormatori, 'fetchFormatori');
        }, 200);
        
        setTimeout(() => {
          safeFetch(fetchAllCertificates, 'fetchAllCertificates');
          safeFetch(fetchPendingPaymentEnrollments, 'fetchPendingPaymentEnrollments');
        }, 500);
        
        setTimeout(() => {
          safeFetch(fetchDetailedMatchAnalyses, 'fetchDetailedMatchAnalyses');
          safeFetch(fetchAthletesCreatedByMaestros, 'fetchAthletesCreatedByMaestros');
        }, 800);
        
        setTimeout(() => {
          safeFetch(fetchAllMatchAnalyses, 'fetchAllMatchAnalyses');
          safeFetch(fetchAllDetailedMatchAnalyses, 'fetchAllDetailedMatchAnalyses');
        }, 1100);
        
        setTimeout(() => {
          safeFetch(fetchEnrollmentDeletionRequests, 'fetchEnrollmentDeletionRequests');
          safeFetch(fetchMaestrosForFilter, 'fetchMaestrosForFilter');
          safeFetch(fetchInconsistentUsers, 'fetchInconsistentUsers');
          safeFetch(fetchIncompleteProfiles, 'fetchIncompleteProfiles');
        }, 1400);
      } else if (currentRole === 'maestro') {
        setLoadingMessage('Caricamento dati maestro...');
        await Promise.all([
          trackProgress(fetchMaestroAthletes, 'fetchMaestroAthletes'),
          trackProgress(fetchMaestroEvents, 'fetchMaestroEvents'),
          trackProgress(fetchAvailableAthletes, 'fetchAvailableAthletes')
        ]);
      } else if (currentRole === 'contabile') {
        // Contabile only needs maestro athletes data
        setLoadingMessage('Caricamento atleti maestri...');
        await safeFetch(fetchAthletesCreatedByMaestros, 'fetchAthletesCreatedByMaestros');
        await safeFetch(fetchMaestrosForFilter, 'fetchMaestrosForFilter');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Only show error if it's a critical failure
    } finally {
      setLoading(false);
      setIsLoadingData(false);
      setTimeout(() => {
        setLoadingProgress(0);
        setLoadingMessage('');
      }, 500);
    }
  };

  const fetchMyEvents = async () => {
    try {
      const response = await axios.get(`${API}/my-events`, { timeout: 15000 });
      setMyEvents(response.data);
    } catch (error) {
      console.error('Error fetching my events:', error);
    }
  };

  const fetchMyEnrollments = async () => {
    try {
      const response = await axios.get(`${API}/my-enrollments`, { timeout: 15000 });
      setMyEnrollments(response.data);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    }
  };

  const fetchMyMatchAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/my-match-analysis`, { timeout: 15000 });
      setMatchAnalyses(response.data);
    } catch (error) {
      console.error('Error fetching match analyses:', error);
    }
  };

  const fetchMyCertificates = async () => {
    try {
      const response = await axios.get(`${API}/my-certificates`, { timeout: 15000 });
      setCertificates(response.data);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`, { timeout: 15000 });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const endpoint = getCurrentRole() === 'teacher' ? `${API}/teacher/athletes` : `${API}/admin/users`;
      const response = await axios.get(endpoint, { timeout: 15000 });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${API}/teachers`, { timeout: 15000 });
      setTeachers(response.data);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const fetchTeacherMatchAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/teacher/match-analysis`, { timeout: 15000 });
      setMatchAnalyses(response.data);
    } catch (error) {
      console.error('Error fetching teacher match analyses:', error);
    }
  };

  const fetchAdminMatchAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/admin/match-analysis`, { timeout: 15000 });
      setAdminMatchAnalyses(response.data);
    } catch (error) {
      console.error('Error fetching admin match analyses:', error);
    }
  };

  const fetchRejectedEnrollments = async () => {
    try {
      const response = await axios.get(`${API}/admin/rejected-enrollments`, { timeout: 15000 });
      setRejectedEnrollments(response.data);
    } catch (error) {
      console.error('Error fetching rejected enrollments:', error);
    }
  };

  const deleteRejectedEnrollment = async (enrollmentId, userName, eventTitle) => {
    if (window.confirm(`Sei sicuro di voler cancellare l'iscrizione rifiutata di "${userName}" per "${eventTitle}"? L'atleta potrà iscriversi di nuovo.`)) {
      try {
        const response = await axios.delete(`${API}/admin/enrollments/${enrollmentId}`);
        toast.success(response.data.message);
        fetchRejectedEnrollments();
        fetchEnrollmentsCount();
      } catch (error) {
        const message = error.response?.data?.detail || 'Errore nella cancellazione';
        toast.error(typeof message === 'string' ? message : 'Errore nella cancellazione');
      }
    }
  };


  // Maestro: fetch available athletes for association
  const fetchAvailableAthletes = async () => {
    try {
      const response = await axios.get(`${API}/maestro/available-athletes`, { timeout: 15000 });
      setAllAvailableAthletes(response.data);
    } catch (error) {
      console.error('Error fetching available athletes:', error);
    }
  };

  const associateAthleteResults = associateAthleteSearch.length >= 2
    ? allAvailableAthletes.filter(a => {
        const q = associateAthleteSearch.toLowerCase();
        return `${a.name} ${a.surname}`.toLowerCase().includes(q) || a.surname.toLowerCase().includes(q) || a.name.toLowerCase().includes(q);
      }).slice(0, 10)
    : [];

  const associateAthlete = async (athleteId) => {
    try {
      const response = await axios.post(`${API}/maestro/associate-athlete/${athleteId}`);
      if (response.data.status === 'conflict') {
        setTransferConflict({ ...response.data, athleteId });
      } else {
        toast.success(response.data.message);
        setAssociateAthleteSearch('');
        fetchMaestroAthletes();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'associazione');
    }
  };

  const confirmTransfer = async (athleteId) => {
    try {
      const response = await axios.post(`${API}/maestro/confirm-transfer/${athleteId}`);
      toast.success(response.data.message);
      setTransferConflict(null);
      setAssociateAthleteSearch('');
      fetchMaestroAthletes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel trasferimento');
    }
  };


  const submitMatchResult = async () => {
    try {
      const DEFAULT_TEXT = 'Vedi Certificato allegato.';
      const payload = {
        event_id: matchResultForm.event_id,
        athlete_id: matchResultForm.athlete_id,
        content: (matchResultForm.content && matchResultForm.content.trim()) ? matchResultForm.content.trim() : DEFAULT_TEXT,
        result_score: parseFloat(matchResultForm.result_score),
        judgments_advice: (matchResultForm.judgments_advice && matchResultForm.judgments_advice.trim()) ? matchResultForm.judgments_advice.trim() : DEFAULT_TEXT,
        certificate_pdf_data: matchResultForm.certificate_pdf_data || null,
        certificate_pdf_filename: matchResultForm.certificate_pdf_filename || null
      };
      await axios.post(`${API}/admin/match-analysis`, payload);
      toast.success('Risultato Match Analysis inserito con successo');
      setMatchResultForm({ event_id: '', athlete_id: '', athlete_search: '', content: 'Vedi Certificato allegato.', result_score: '', judgments_advice: 'Vedi Certificato allegato.', certificate_pdf_data: null, certificate_pdf_filename: null });
      fetchAdminMatchAnalyses();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nell\'inserimento del risultato';
      toast.error(typeof message === 'string' ? message : 'Errore nell\'inserimento');
    }
  };

  const fetchFormatori = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/formatori`, {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 15000
      });
      setFormatori(response.data);
    } catch (error) {
      console.error('Error fetching formatori:', error);
      setFormatori([]);
    }
  };

  // Admin fetch functions
  const fetchAllMatchAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-match-analysis`, { timeout: 15000 });
      setAllMatchAnalyses(response.data);
    } catch (error) {
      console.error('Error fetching all match analyses:', error);
    }
  };

  const fetchAllDetailedMatchAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-detailed-match-analysis`, { timeout: 15000 });
      setAllDetailedMatchAnalyses(response.data);
    } catch (error) {
      console.error('Error fetching all detailed analyses:', error);
    }
  };

  const fetchAllCertificates = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-certificates`, { timeout: 15000 });
      setAllCertificates(response.data);
    } catch (error) {
      console.error('Error fetching all certificates:', error);
    }
  };
  // Formatore events functions
  const fetchFormatoreEvents = async () => {
    try {
      const response = await axios.get(`${API}/formatore/my-events`, { timeout: 15000 });
      setTeacherEvents(response.data);
    } catch (error) {
      console.error('Error fetching formatore events:', error);
    }
  };

  // Teacher events functions
  const fetchTeacherEvents = async () => {
    try {
      const response = await axios.get(`${API}/teacher/my-events`, { timeout: 15000 });
      setTeacherEvents(response.data);
    } catch (error) {
      console.error('Error fetching teacher events:', error);
    }
  };

  const fetchTeacherEventEnrollments = async (eventId) => {
    try {
      const response = await axios.get(`${API}/teacher/events/${eventId}/enrollments`, { timeout: 15000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching teacher event enrollments:', error);
      return [];
    }
  };

  const fetchFormatoreEventEnrollments = async (eventId) => {
    try {
      const response = await axios.get(`${API}/formatore/events/${eventId}/enrollments`, { timeout: 15000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching formatore event enrollments:', error);
      return [];
    }
  };

  // Maestro functions
  const fetchMaestroAthletes = async () => {
    try {
      const response = await axios.get(`${API}/maestro/athletes`);
      setMaestroAthletes(response.data);
    } catch (error) {
      console.error('Error fetching maestro athletes:', error);
    }
  };

  const fetchMaestroEvents = async () => {
    try {
      const response = await axios.get(`${API}/maestro/events`);
      setMaestroEvents(response.data);
    } catch (error) {
      console.error('Error fetching maestro events:', error);
    }
  };

  const fetchEnrollmentDeletionRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/enrollment-deletion-requests`);
      setEnrollmentDeletionRequests(response.data);
    } catch (error) {
      console.error('Error fetching deletion requests:', error);
    }
  };

  const fetchAthletesCreatedByMaestros = async (maestroFilter = null) => {
    try {
      const params = maestroFilter && maestroFilter !== 'all' ? `?maestro_id=${maestroFilter}` : '';
      const response = await axios.get(`${API}/admin/athletes-created-by-maestros${params}`);
      setAthletesCreatedByMaestros(response.data);
    } catch (error) {
      console.error('Error fetching athletes created by maestros:', error);
    }
  };

  const fetchMaestrosForFilter = async () => {
    try {
      const response = await axios.get(`${API}/admin/maestros-list`);
      setMaestrosForFilter(response.data);
    } catch (error) {
      console.error('Error fetching maestros for filter:', error);
    }
  };

  const downloadPaymentPdf = async (creationId, filename) => {
    try {
      const response = await axios.get(`${API}/admin/maestro-athlete-creation/${creationId}/payment-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `pagamento_${creationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore nel download del PDF');
    }
  };

  const exportMaestroAthletesExcel = async () => {
    try {
      const params = selectedMaestroFilter && selectedMaestroFilter !== 'all' ? `?maestro_id=${selectedMaestroFilter}` : '';
      const response = await axios.get(`${API}/admin/athletes-created-by-maestros/export-excel${params}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `atleti_maestri_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel esportato con successo!');
    } catch (error) {
      toast.error('Errore nell\'esportazione Excel');
    }
  };

  const approveMaestroAthleteCreation = async (creationId) => {
    try {
      await axios.patch(`${API}/admin/maestro-athlete-creation/${creationId}/approve`);
      toast.success('Atleta approvato con successo!');
      fetchAthletesCreatedByMaestros(selectedMaestroFilter);
      fetchUsers(); // Refresh user list
    } catch (error) {
      toast.error('Errore nell\'approvazione');
    }
  };

  // Password validation: min 8 chars, 1 number, 1 special char
  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(password);
    return {
      isValid: minLength && hasNumber && hasSpecial,
      errors: {
        minLength: !minLength ? 'Almeno 8 caratteri' : null,
        hasNumber: !hasNumber ? 'Almeno 1 numero' : null,
        hasSpecial: !hasSpecial ? 'Almeno 1 carattere speciale' : null
      }
    };
  };

  const checkDuplicateAthleteAndCreate = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!newAthleteData.name?.trim() || !newAthleteData.surname?.trim() || 
        !newAthleteData.email?.trim() || !newAthleteData.password) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    // Validate password
    const passwordValidation = validatePassword(newAthleteData.password);
    if (!passwordValidation.isValid) {
      const errorMessages = Object.values(passwordValidation.errors).filter(e => e).join(', ');
      toast.error(`Password non valida: ${errorMessages}`);
      return;
    }
    
    // Validate PDF file
    if (!newAthleteData.payment_pdf) {
      toast.error('Devi caricare il PDF della copia del pagamento');
      return;
    }
    
    try {
      // Check if athlete with same name and surname exists
      const response = await axios.get(`${API}/maestro/check-duplicate-athlete`, {
        params: {
          name: newAthleteData.name.trim(),
          surname: newAthleteData.surname.trim()
        }
      });
      
      if (response.data.exists) {
        // Show duplicate dialog
        setDuplicateAthleteInfo(response.data.athlete);
        setShowDuplicateAthleteDialog(true);
      } else {
        // No duplicate, create athlete directly
        await createAthleteByMaestro();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante la verifica';
      toast.error(message);
    }
  };

  const createAthleteByMaestro = async () => {
    try {
      // Use FormData for file upload
      const formData = new FormData();
      formData.append('name', newAthleteData.name.trim());
      formData.append('surname', newAthleteData.surname.trim());
      formData.append('email', newAthleteData.email.trim());
      formData.append('password', newAthleteData.password);
      formData.append('payment_pdf', newAthleteData.payment_pdf);
      
      await axios.post(`${API}/maestro/athletes`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Richiesta atleta inviata! In attesa di approvazione admin.');
      resetAthleteForm();
      fetchMaestroAthletes();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante la creazione dell\'atleta';
      toast.error(message);
    }
  };

  const overwriteAthletePassword = async () => {
    if (!duplicateAthleteInfo) return;
    
    try {
      await axios.post(`${API}/maestro/overwrite-athlete-password`, {
        athlete_id: duplicateAthleteInfo.id,
        new_password: newAthleteData.password
      });
      toast.success('Password aggiornata e atleta aggiunto alla tua lista!');
      setShowDuplicateAthleteDialog(false);
      setDuplicateAthleteInfo(null);
      resetAthleteForm();
      fetchMaestroAthletes();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'aggiornamento';
      toast.error(message);
    }
  };

  const resetAthleteForm = () => {
    setShowCreateAthleteModal(false);
    setShowDuplicateAthleteDialog(false);
    setDuplicateAthleteInfo(null);
    setNewAthleteData({
      email: '',
      password: '',
      name: '',
      surname: '',
      payment_confirmed: false,
      payment_pdf: null
    });
  };

  const enrollAthletesInEvent = async () => {
    if (!selectedEventForEnrollment || selectedAthletesForEnrollment.length === 0) {
      toast.error('Seleziona un evento e almeno un atleta');
      return;
    }
    try {
      const response = await axios.post(`${API}/maestro/events/${selectedEventForEnrollment}/enroll`, {
        athlete_ids: selectedAthletesForEnrollment
      });
      
      if (response.data.enrolled.length > 0) {
        toast.success(`${response.data.enrolled.length} atleti iscritti a "${response.data.event_title}"`);
      }
      if (response.data.errors.length > 0) {
        response.data.errors.forEach(err => {
          toast.error(`Atleta ${err.athlete_id}: ${err.error}`);
        });
      }
      
      setShowEnrollmentModal(false);
      setSelectedAthletesForEnrollment([]);
      setSelectedEventForEnrollment(null);
      fetchMaestroAthletes();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'iscrizione';
      toast.error(message);
    }
  };

  const requestEnrollmentDeletion = async () => {
    if (!selectedEnrollmentForDeletion || !deletionReason.trim()) {
      toast.error('Inserisci un motivo per la richiesta');
      return;
    }
    try {
      await axios.post(`${API}/maestro/enrollment-deletion-request`, {
        enrollment_id: selectedEnrollmentForDeletion.enrollment_id,
        reason: deletionReason.trim()
      });
      toast.success('Richiesta di eliminazione inviata all\'admin');
      setShowDeletionRequestModal(false);
      setSelectedEnrollmentForDeletion(null);
      setDeletionReason('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'invio della richiesta';
      toast.error(message);
    }
  };

  const processEnrollmentDeletionRequest = async (requestId, action) => {
    try {
      await axios.patch(`${API}/admin/enrollment-deletion-requests/${requestId}?action=${action}`);
      toast.success(action === 'approve' ? 'Richiesta approvata' : 'Richiesta rifiutata');
      fetchEnrollmentDeletionRequests();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'elaborazione';
      toast.error(message);
    }
  };

  // User management functions
  const createUser = async (e) => {
    e.preventDefault();
    try {
      const userData = {
        email: newUser.email,
        password: newUser.password,
        name: newUser.name,
        surname: newUser.surname,
        roles: newUser.roles
      };
      
      await axios.post(`${API}/admin/users`, userData);
      toast.success('Utente creato con successo!');
      
      // Reset form
      setNewUser({
        name: '',
        surname: '',
        email: '',
        password: '',
        role: 'atleta',
        roles: ['atleta']
      });
      
      // Refresh users list
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante la creazione dell\'utente';
      toast.error(message);
    }
  };

  // Password reset functions
  const openPasswordResetModal = (userId, userEmail) => {
    setPasswordResetModal({
      isOpen: true,
      userId,
      userEmail,
      newPassword: ''
    });
  };

  const closePasswordResetModal = () => {
    setPasswordResetModal({
      isOpen: false,
      userId: null,
      userEmail: '',
      newPassword: ''
    });
  };

  const resetUserPassword = async () => {
    try {
      if (passwordResetModal.newPassword.length < 6) {
        toast.error('La password deve essere di almeno 6 caratteri');
        return;
      }

      await axios.patch(`${API}/admin/users/${passwordResetModal.userId}/reset-password`, {
        new_password: passwordResetModal.newPassword
      });
      
      toast.success(`Password resetata con successo per ${passwordResetModal.userEmail}`);
      closePasswordResetModal();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nel reset della password';
      toast.error(message);
    }
  };

  // Delete functions for admin
  const deleteMatchAnalysis = async (analysisId) => {
    if (window.confirm('Sei sicuro di voler eliminare questa match analysis? Questa azione non può essere annullata.')) {
      try {
        await axios.delete(`${API}/admin/match-analysis/${analysisId}`);
        toast.success('Match analysis eliminata con successo');
        fetchAllMatchAnalyses(); // Refresh list
      } catch (error) {
        toast.error('Errore nell\'eliminazione della match analysis');
      }
    }
  };

  const deleteDetailedMatchAnalysis = async (analysisId) => {
    if (window.confirm('Sei sicuro di voler eliminare questa analisi dettagliata? Questa azione non può essere annullata.')) {
      try {
        await axios.delete(`${API}/admin/detailed-match-analysis/${analysisId}`);
        toast.success('Analisi dettagliata eliminata con successo');
        fetchAllDetailedMatchAnalyses(); // Refresh list
      } catch (error) {
        toast.error('Errore nell\'eliminazione dell\'analisi dettagliata');
      }
    }
  };

  const deleteCertificate = async (certificateId) => {
    if (window.confirm('Sei sicuro di voler eliminare questo certificato? Questa azione non può essere annullata e il file PDF verrà eliminato definitivamente.')) {
      try {
        await axios.delete(`${API}/admin/certificates/${certificateId}`);
        toast.success('Certificato eliminato con successo');
        fetchAllCertificates(); // Refresh list
      } catch (error) {
        toast.error('Errore nell\'eliminazione del certificato');
      }
    }
  };

  // Regenerate certificate with new PDF template
  const regenerateCertificate = async (certificateId, file) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('pdf_file', file);
      
      const response = await axios.post(`${API}/admin/certificates/${certificateId}/regenerate`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Certificato rigenerato con successo!');
      fetchAllCertificates(); // Refresh list
    } catch (error) {
      console.error('Error regenerating certificate:', error);
      toast.error('Errore nella rigenerazione del certificato');
    }
  };

  // Study materials functions
  const fetchStudyMaterials = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/events/${eventId}/study-materials`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setStudyMaterials(response.data);
    } catch (error) {
      console.error('Error fetching study materials:', error);
      toast.error('Errore nel caricamento del materiale didattico');
    }
  };

  const uploadStudyMaterial = async (eventId) => {
    if (!materialUploadForm.title || !materialUploadForm.file) {
      toast.error('Titolo e file sono obbligatori');
      return;
    }

    const formData = new FormData();
    formData.append('title', materialUploadForm.title);
    formData.append('description', materialUploadForm.description);
    formData.append('file', materialUploadForm.file);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/events/${eventId}/study-materials`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Materiale caricato con successo');
      setMaterialUploadForm({ title: '', description: '', file: null });
      fetchStudyMaterials(eventId); // Refresh list
    } catch (error) {
      console.error('Error uploading study material:', error);
      toast.error(error.response?.data?.detail || 'Errore nel caricamento del materiale');
    }
  };

  const downloadStudyMaterial = async (materialId, filename) => {
    try {
      const response = await axios.get(`${API}/study-materials/${materialId}/download`, {
        responseType: 'blob'
      });
      // Verify the response is actually a file (not a JSON error blob)
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        // Backend returned JSON inside a blob — parse it to surface the real error
        const text = await response.data.text();
        let parsed = {};
        try { parsed = JSON.parse(text); } catch {}
        throw new Error(parsed.detail || 'Risposta inattesa dal server');
      }
      const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `materiale_${materialId}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (error) {
      let detail = error.message || 'Errore nel download del materiale';
      // axios error with blob response containing JSON detail
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) detail = parsed.detail;
        } catch { /* keep generic */ }
      } else if (error.response?.data?.detail) {
        detail = error.response.data.detail;
      }
      const status = error.response?.status;
      // eslint-disable-next-line no-console
      console.error('[downloadStudyMaterial]', { materialId, status, detail, error });
      toast.error(status ? `Download fallito (${status}): ${detail}` : `Download fallito: ${detail}`);
    }
  };

  const deleteStudyMaterial = async (materialId) => {
    if (window.confirm('Sei sicuro di voler eliminare questo materiale didattico? Questa azione non può essere annullata.')) {
      try {
        await axios.delete(`${API}/study-materials/${materialId}`);
        toast.success('Materiale eliminato con successo');
        fetchStudyMaterials(selectedEventForMaterials.id); // Refresh list
      } catch (error) {
        toast.error('Errore nell\'eliminazione del materiale');
      }
    }
  };

  // Check if athlete profile is complete
  const checkAthleteProfileComplete = useCallback((user) => {
    if (!user || !user.roles || !user.roles.includes('atleta')) {
      setIsAthleteProfileComplete(true);
      return;
    }
    
    const requiredFields = ['birth_date', 'fiscal_code', 'athlete_class', 'asa_reference', 'phone', 'address', 'region'];
    const missingFields = requiredFields.filter(field => !user[field] || user[field].toString().trim() === '');
    
    // Check disciplines: at least one discipline selected OR disciplines_other filled
    const hasDisciplines = user.disciplines && Array.isArray(user.disciplines) && user.disciplines.length > 0;
    const hasDisciplinesOther = user.disciplines_other && user.disciplines_other.trim() !== '';
    const disciplinesMissing = !hasDisciplines && !hasDisciplinesOther;
    
    const isComplete = missingFields.length === 0 && !disciplinesMissing;
    setIsAthleteProfileComplete(isComplete);
    
    // Force show profile completion modal if incomplete
    if (!isComplete) {
      setShowMandatoryProfileModal(true);
    }
  }, []);

  const checkMaestroProfileComplete = useCallback((user) => {
    if (!user || !user.roles || !user.roles.includes('maestro')) return;
    if (user.roles.includes('admin')) return;
    const fields = ['school_name', 'school_address', 'school_phone', 'school_email', 'school_vat'];
    const missing = fields.filter(f => !user[f] || user[f].toString().trim() === '');
    if (missing.length > 0) {
      setShowMaestroProfileModal(true);
    }
  }, []);

  const saveMaestroProfile = async () => {
    const fields = {
      school_name: 'Nome della Scuola',
      school_address: 'Indirizzo della Scuola',
      school_phone: 'Telefono di Contatto',
      school_email: 'Email di Contatto',
      school_vat: 'P.IVA o CF della Scuola'
    };
    const missing = Object.entries(fields).filter(([k]) => !maestroProfileData[k] || !maestroProfileData[k].trim()).map(([, v]) => v);
    if (missing.length > 0) {
      toast.error(`Compila i campi obbligatori: ${missing.join(', ')}`);
      return;
    }
    try {
      await axios.patch(`${API}/profiles/me/maestro`, maestroProfileData);
      toast.success('Profilo scuola salvato con successo');
      setShowMaestroProfileModal(false);
      const meResponse = await axios.get(`${API}/auth/me`);
      if (meResponse.data) {
        checkMaestroProfileComplete(meResponse.data);
      }
    } catch (error) {
      toast.error('Errore nel salvataggio del profilo scuola');
    }
  };

  // Save mandatory profile data
  const saveMandatoryProfile = async () => {
    // Validate all fields are filled
    const requiredLabels = {
      birth_date: 'Data di Nascita',
      fiscal_code: 'Codice Fiscale',
      athlete_class: 'Classe di Appartenenza',
      asa_reference: 'ASA di Riferimento',
      phone: 'Telefono',
      address: 'Indirizzo',
      region: 'Regione'
    };
    
    const missingLabels = Object.entries(requiredLabels)
      .filter(([key]) => !mandatoryProfileData[key] || mandatoryProfileData[key].toString().trim() === '')
      .map(([, label]) => label);

    // Check disciplines
    const hasDisciplines = mandatoryProfileData.disciplines && mandatoryProfileData.disciplines.length > 0;
    const hasDisciplinesOther = mandatoryProfileData.disciplines_other && mandatoryProfileData.disciplines_other.trim() !== '';
    if (!hasDisciplines && !hasDisciplinesOther) {
      missingLabels.push('Discipline (seleziona almeno una disciplina o compila il campo Altro)');
    }
    
    if (missingLabels.length > 0) {
      toast.error(`Compila i seguenti campi: ${missingLabels.join(', ')}`);
      return;
    }

    // Validate fiscal code format (16 chars)
    if (mandatoryProfileData.fiscal_code.length !== 16) {
      toast.error('Il Codice Fiscale deve essere di 16 caratteri');
      return;
    }

    // Validate birth date format (DD/MM/YYYY)
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(mandatoryProfileData.birth_date)) {
      toast.error('La Data di Nascita deve essere nel formato GG/MM/AAAA');
      return;
    }

    setSavingMandatoryProfile(true);
    try {
      const updateData = {
        birth_date: mandatoryProfileData.birth_date.trim(),
        fiscal_code: mandatoryProfileData.fiscal_code.toUpperCase().trim(),
        athlete_class: mandatoryProfileData.athlete_class,
        asa_reference: mandatoryProfileData.asa_reference.trim(),
        phone: mandatoryProfileData.phone.trim(),
        address: mandatoryProfileData.address.trim(),
        region: mandatoryProfileData.region,
        disciplines: mandatoryProfileData.disciplines,
        disciplines_other: mandatoryProfileData.disciplines_other.trim()
      };
      
      await axios.patch(`${API}/profiles/me/athlete`, updateData);
      toast.success('Profilo atleta completato con successo!');
      setShowMandatoryProfileModal(false);
      setIsAthleteProfileComplete(true);
      // Reload to update user context
      window.location.reload();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nel salvataggio del profilo';
      toast.error(message);
    } finally {
      setSavingMandatoryProfile(false);
    }
  };


  // Online meeting links functions
  const updateOnlineMeetingLinks = async (eventId) => {
    try {
      const params = new URLSearchParams();
      if (onlineMeetingLinks.online_meeting_link_1) params.append('online_meeting_link_1', onlineMeetingLinks.online_meeting_link_1);
      if (onlineMeetingLinks.online_meeting_datetime_1) params.append('online_meeting_datetime_1', onlineMeetingLinks.online_meeting_datetime_1);
      if (onlineMeetingLinks.online_meeting_link_2) params.append('online_meeting_link_2', onlineMeetingLinks.online_meeting_link_2);
      if (onlineMeetingLinks.online_meeting_datetime_2) params.append('online_meeting_datetime_2', onlineMeetingLinks.online_meeting_datetime_2);
      if (onlineMeetingLinks.online_meeting_link_3) params.append('online_meeting_link_3', onlineMeetingLinks.online_meeting_link_3);
      if (onlineMeetingLinks.online_meeting_datetime_3) params.append('online_meeting_datetime_3', onlineMeetingLinks.online_meeting_datetime_3);
      if (onlineMeetingLinks.online_meeting_link_4) params.append('online_meeting_link_4', onlineMeetingLinks.online_meeting_link_4);
      if (onlineMeetingLinks.online_meeting_datetime_4) params.append('online_meeting_datetime_4', onlineMeetingLinks.online_meeting_datetime_4);
      
      await axios.patch(`${API}/formatore/events/${eventId}/online-links?${params.toString()}`);
      toast.success('Link incontri online aggiornati con successo');
      setSelectedEventForLinks(null);
      // Refresh events
      fetchFormatoreEvents();
    } catch (error) {
      console.error('Error updating online meeting links:', error);
      toast.error('Errore nell\'aggiornamento dei link');
    }
  };

  const approveUser = async (userId) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/approve`);
      toast.success('Utente approvato con successo');
      fetchUsers();
    } catch (error) {
      toast.error('Errore nell\'approvazione dell\'utente');
    }
  };

  const createEvent = async (e) => {
    e.preventDefault();
    try {
      let posterUrl = newEvent.poster_url;
      let posterData = null;
      let posterContentType = null;
      
      // Upload poster if file is selected
      if (posterFile) {
        setUploadingPoster(true);
        const formData = new FormData();
        formData.append('file', posterFile);
        
        const uploadResponse = await axios.post(`${API}/upload-poster`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        posterUrl = uploadResponse.data.poster_url;
        posterData = uploadResponse.data.poster_data;
        posterContentType = uploadResponse.data.poster_content_type;
        setUploadingPoster(false);
      }
      
      const cleanData = {
        ...newEvent,
        teacher_ids: newEvent.teacher_ids || [],
        external_teachers_text: newEvent.external_teachers_text || '',
        poster_url: posterUrl || null,
        poster_data: posterData,
        poster_content_type: posterContentType,
        // Handle date_tbd: send null for dates when flag is set
        start_date: newEvent.date_tbd ? null : (newEvent.start_date || null),
        end_date: newEvent.date_tbd ? null : (newEvent.end_date || null)
      };
      
      console.log('Creating event with data:', cleanData);
      await axios.post(`${API}/events`, cleanData);
      toast.success('Evento creato con successo');
      setNewEvent({
        title: '',
        description: '',
        type: 'stage',
        date_tbd: false,
        start_date: '',
        end_date: '',
        teacher_ids: [],
        external_teachers_text: '',
        location: '',
        max_participants: 20,
        poster_url: '',
        dance_analysis: false
      });
      setPosterFile(null);
      fetchEvents();
    } catch (error) {
      setUploadingPoster(false);
      console.error('Create event error:', error);
      let message = 'Errore nella creazione dell\'evento';
      
      if (error.response?.data) {
        if (Array.isArray(error.response.data)) {
          message = error.response.data.map(err => err.msg || err.message || err).join(', ');
        } else if (typeof error.response.data === 'object') {
          message = error.response.data.detail || error.response.data.message || message;
        } else {
          message = error.response.data;
        }
      }
      
      // Ensure message is a string
      const safeMessage = typeof message === 'string' ? message : 'Errore nell\'aggiornamento dell\'evento';
      console.error('Error message:', safeMessage);
      toast.error(safeMessage);
    }
  };

  const createMatchAnalysis = async (e) => {
    e.preventDefault();
    
    // Validate fields based on type
    if (newMatchAnalysis.is_private_lesson) {
      if (!newMatchAnalysis.athlete_name || !newMatchAnalysis.athlete_surname) {
        toast.error('Nome e cognome atleta sono obbligatori per lezioni private');
        return;
      }
    } else {
      if (!newMatchAnalysis.event_id || !newMatchAnalysis.athlete_id) {
        toast.error('Evento e atleta sono obbligatori');
        return;
      }
    }
    
    if (!newMatchAnalysis.content) {
      toast.error('Il contenuto è obbligatorio');
      return;
    }
    
    try {
      await axios.post(`${API}/match-analysis`, newMatchAnalysis);
      toast.success('Match Analysis creata con successo');
      setNewMatchAnalysis({ 
        event_id: '', 
        athlete_id: '', 
        content: '',
        is_private_lesson: false,
        athlete_name: '',
        athlete_surname: ''
      });
      fetchTeacherMatchAnalyses();
    } catch (error) {
      toast.error('Errore nella creazione della Match Analysis');
    }
  };

  const createCertificate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/certificates`, newCertificate);
      toast.success('Certificato creato con successo');
      setNewCertificate({ user_id: '', title: '', description: '' });
    } catch (error) {
      toast.error('Errore nella creazione del certificato');
    }
  };

  const deleteUser = async (userId, userName) => {
    if (window.confirm(`Sei sicuro di voler eliminare l'utente ${userName}? Questa azione non può essere annullata.`)) {
      try {
        await axios.delete(`${API}/admin/users/${userId}`);
        toast.success('Utente eliminato con successo');
        fetchUsers();
      } catch (error) {
        const message = error.response?.data?.detail || 'Errore nell\'eliminazione dell\'utente';
        // Ensure message is a string
        const safeMessage = typeof message === 'string' ? message : 'Errore nell\'aggiornamento dell\'evento';
        console.error('Error message:', safeMessage);
        toast.error(safeMessage);
      }
    }
  };

  // Filter functions for match analyses
  const filterMatchAnalyses = (analyses) => {
    return analyses.filter(analysis => {
      const matchesDate = !analysisFilters.date || 
        new Date(analysis.created_at).toISOString().split('T')[0] === analysisFilters.date;
      
      const matchesEvent = !analysisFilters.event || 
        analysis.event_title.toLowerCase().includes(analysisFilters.event.toLowerCase());
      
      const matchesAthlete = !analysisFilters.athlete || 
        analysis.athlete_name.toLowerCase().includes(analysisFilters.athlete.toLowerCase());
      
      return matchesDate && matchesEvent && matchesAthlete;
    });
  };

  const clearAnalysisFilters = () => {
    setAnalysisFilters({
      date: '',
      event: '',
      athlete: ''
    });
  };

  // Certificate filter functions
  const filterCertificates = (certificates) => {
    return certificates.filter(cert => {
      const matchesEvent = !certificateFilters.event || 
        cert.title.toLowerCase().includes(certificateFilters.event.toLowerCase());
      
      const matchesAthlete = !certificateFilters.athlete || 
        cert.user_name.toLowerCase().includes(certificateFilters.athlete.toLowerCase());
      
      return matchesEvent && matchesAthlete;
    });
  };

  const clearCertificateFilters = () => {
    setCertificateFilters({
      event: '',
      athlete: ''
    });
  };

  const fetchDetailedMatchAnalyses = async () => {
    let endpoint = '';
    if (getCurrentRole() === 'admin') {
      endpoint = `${API}/admin/all-detailed-match-analysis`;
    } else if (getCurrentRole() === 'teacher') {
      endpoint = `${API}/teacher/detailed-match-analysis`;
    } else {
      endpoint = `${API}/my-detailed-match-analysis`;
    }
    
    const response = await axios.get(endpoint);
    setDetailedMatchAnalyses(response.data);
  };

  const createDetailedMatchAnalysis = async (e) => {
    e.preventDefault();
    
    // Validate fields based on type
    if (newDetailedAnalysis.is_private_lesson) {
      if (!newDetailedAnalysis.athlete_name || !newDetailedAnalysis.athlete_surname) {
        toast.error('Nome e cognome atleta sono obbligatori per lezioni private');
        return;
      }
    } else {
      if (!newDetailedAnalysis.event_id || !newDetailedAnalysis.athlete_id) {
        toast.error('Evento e atleta sono obbligatori');
        return;
      }
    }
    
    try {
      await axios.post(`${API}/detailed-match-analysis`, newDetailedAnalysis);
      toast.success('Match Analysis dettagliata creata con successo');
      setNewDetailedAnalysis({
        event_id: '',
        athlete_id: '',
        is_private_lesson: false,
        athlete_name: '',
        athlete_surname: '',
        scores: {
          tempo: 5,
          ritmo: 5,
          posizione_piedi: 5,
          equilibrio: 5,
          correttezza_eleganza: 5,
          spinta_slancio: 5,
          rotazione_testa: 5,
          eleganza_estetica: 5,
          fluidita_agilita: 5,
          espressivita_carattere: 5,
          musicalita_interpretazione: 5,
          stile_portamento: 5
        },
        notes: ''
      });
      fetchDetailedMatchAnalyses();
    } catch (error) {
      toast.error('Errore nella creazione della Match Analysis');
    }
  };

  const sendAnalysisEmail = async (analysisId) => {
    // Open modal to get email
    const analysis = detailedMatchAnalyses.find(a => a.id === analysisId);
    setEmailModal({
      isOpen: true,
      analysisId: analysisId,
      athleteName: analysis?.athlete_name || 'Atleta',
      athleteEmail: ''
    });
  };

  const confirmSendEmail = async () => {
    if (!emailModal.athleteEmail) {
      toast.error('Inserisci un indirizzo email valido');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/detailed-match-analysis/${emailModal.analysisId}/send-email`,
        {
          recipient_email: emailModal.athleteEmail
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      toast.success(`Email inviata con successo a ${emailModal.athleteEmail}`);
      setEmailModal({ isOpen: false, analysisId: null, athleteName: '', athleteEmail: '' });
    } catch (error) {
      console.error('Error sending email:', error);
      const message = error.response?.data?.detail || 'Errore nell\'invio dell\'email';
      toast.error(message);
    }
  };

  const searchUsers = async () => {
    // Check if at least one filter is provided
    if (!searchFilters.name && !searchFilters.surname && !searchFilters.email && !searchFilters.fiscal_code && !searchFilters.asa_reference && !searchFilters.maestro_id) {
      toast.error('Inserisci almeno un criterio di ricerca');
      return;
    }

    setIsSearching(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (searchFilters.name) params.append('name', searchFilters.name);
      if (searchFilters.surname) params.append('surname', searchFilters.surname);
      if (searchFilters.email) params.append('email', searchFilters.email);
      if (searchFilters.fiscal_code) params.append('fiscal_code', searchFilters.fiscal_code);
      if (searchFilters.asa_reference) params.append('asa_reference', searchFilters.asa_reference);
      if (searchFilters.maestro_id) params.append('maestro_id', searchFilters.maestro_id);
      
      const response = await axios.get(`${API}/admin/search-users?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setSearchResults(response.data);
      if (response.data.length === 0) {
        toast.info('Nessun utente trovato con i criteri specificati');
      } else {
        toast.success(`Trovati ${response.data.length} utenti`);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Errore nella ricerca');
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearchFilters = () => {
    setSearchFilters({
      name: '',
      surname: '',
      email: '',
      fiscal_code: '',
      asa_reference: '',
      maestro_id: ''
    });
    setSearchResults([]);
    toast.info('Filtri puliti');
  };

  const assignTechnicalCredits = async () => {
    if (!technicalCreditsModal.athleteId) {
      toast.error('Seleziona un atleta');
      return;
    }
    
    const creditsValue = parseInt(technicalCreditsModal.credits);
    if (!creditsValue || creditsValue < 1) {
      toast.error('Inserisci un numero di crediti valido (minimo 1)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/technical-credits`,
        {
          athlete_id: technicalCreditsModal.athleteId,
          credits: creditsValue,
          academic_year: technicalCreditsModal.academicYear,
          notes: technicalCreditsModal.notes || null
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      toast.success(`${creditsValue} crediti tecnici assegnati a ${technicalCreditsModal.athleteName}`);
      setTechnicalCreditsModal({
        isOpen: false,
        athleteId: '',
        athleteName: '',
        credits: '',
        academicYear: '2025/2026',
        notes: ''
      });
    } catch (error) {
      console.error('Error assigning technical credits:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'assegnazione dei crediti');
    }
  };

  const createCertificateWithFile = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('user_id', newCertificate.user_id);
      formData.append('title', newCertificate.title);
      formData.append('description', newCertificate.description || '');
      if (certificateFile) {
        formData.append('pdf_file', certificateFile);
      }

      await axios.post(`${API}/certificates`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success('Certificato creato con successo');
      setNewCertificate({ user_id: '', title: '', description: '' });
      setCertificateFile(null);
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nella creazione del certificato';
      // Ensure message is a string
      const safeMessage = typeof message === 'string' ? message : 'Errore nell\'aggiornamento dell\'evento';
      console.error('Error message:', safeMessage);
      toast.error(safeMessage);
    }
  };

  const updateEvent = async (eventId) => {
    try {
      let posterUrl = editEventData.poster_url;
      let posterData = null;
      let posterContentType = null;
      
      // Upload new poster if file is selected
      if (editPosterFile) {
        const formData = new FormData();
        formData.append('file', editPosterFile);
        
        const uploadResponse = await axios.post(`${API}/upload-poster`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        posterUrl = uploadResponse.data.poster_url;
        posterData = uploadResponse.data.poster_data;
        posterContentType = uploadResponse.data.poster_content_type;
      }
      
      // Clean the data before sending
      const cleanData = {
        ...editEventData,
        teacher_ids: Array.isArray(editEventData.teacher_ids) ? editEventData.teacher_ids : [],
        external_teachers_text: editEventData.external_teachers_text || '',
        poster_url: posterUrl || null
      };
      
      // Handle date_tbd: clear dates when flag is set
      if (cleanData.date_tbd) {
        cleanData.start_date = null;
        cleanData.end_date = null;
      }
      
      // Add poster data if we uploaded a new poster
      if (posterData) {
        cleanData.poster_data = posterData;
        cleanData.poster_content_type = posterContentType;
      }
      
      // Remove undefined values, but keep nulls for date_tbd fields
      Object.keys(cleanData).forEach(key => {
        if (cleanData[key] === undefined) {
          delete cleanData[key];
        }
        // Only remove null if NOT date fields when date_tbd is set
        if (cleanData[key] === null && !(cleanData.date_tbd && (key === 'start_date' || key === 'end_date'))) {
          delete cleanData[key];
        }
      });
      
      console.log('Sending update data:', cleanData);
      await axios.patch(`${API}/admin/events/${eventId}`, cleanData);
      toast.success('Evento aggiornato con successo');
      setEditingEvent(null);
      setEditEventData({});
      setEditPosterFile(null);
      fetchEvents();
    } catch (error) {
      console.error('Update event error:', error);
      let message = 'Errore nell\'aggiornamento dell\'evento';
      
      if (error.response?.data) {
        // Handle validation errors
        if (Array.isArray(error.response.data)) {
          message = error.response.data.map(err => {
            if (typeof err === 'object' && err !== null) {
              return err.msg || err.message || JSON.stringify(err);
            }
            return String(err);
          }).join(', ');
        } else if (typeof error.response.data === 'object' && error.response.data !== null) {
          if (error.response.data.detail) {
            message = error.response.data.detail;
          } else {
            // Convert object to string safely
            message = JSON.stringify(error.response.data);
          }
        } else {
          message = String(error.response.data);
        }
      }
      
      // Ensure message is a string
      const safeMessage = typeof message === 'string' ? message : 'Errore nell\'aggiornamento dell\'evento';
      console.error('Error message:', safeMessage);
      toast.error(safeMessage);
    }
  };

  const deleteEvent = async (eventId, eventTitle) => {
    if (window.confirm(`Sei sicuro di voler eliminare l'evento "${eventTitle}"? Questa azione non può essere annullata.`)) {
      try {
        await axios.delete(`${API}/admin/events/${eventId}`);
        toast.success('Evento eliminato con successo');
        fetchEvents();
      } catch (error) {
        const message = error.response?.data?.detail || 'Errore nell\'eliminazione dell\'evento';
        // Ensure message is a string
        const safeMessage = typeof message === 'string' ? message : 'Errore nell\'aggiornamento dell\'evento';
        console.error('Error message:', safeMessage);
        toast.error(safeMessage);
      }
    }
  };

  const toggleEventEnrollment = async (eventId, eventTitle, currentClosed) => {
    const action = currentClosed ? 'riaprire' : 'chiudere';
    if (window.confirm(`Sei sicuro di voler ${action} le iscrizioni per "${eventTitle}"?`)) {
      try {
        const response = await axios.patch(`${API}/admin/events/${eventId}/toggle-enrollment`);
        toast.success(response.data.message);
        fetchEvents();
      } catch (error) {
        const message = error.response?.data?.detail || 'Errore nel toggle iscrizioni';
        toast.error(typeof message === 'string' ? message : 'Errore nel toggle iscrizioni');
      }
    }
  };

  const generateEventCertificates = async (eventId, eventTitle) => {
    setGeneratingCertificates(eventId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/events/${eventId}/generate-certificates`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.generated > 0) {
        toast.success(`${response.data.generated} attestati generati con successo!`);
      } else {
        toast.info(response.data.message);
      }
      
      // Update certificates status
      fetchEventCertificatesStatus(eventId);
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nella generazione degli attestati';
      toast.error(message);
    } finally {
      setGeneratingCertificates(null);
    }
  };

  const regenerateMissingCertificates = async (eventId, eventTitle) => {
    setRegeneratingCertificates(eventId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/events/${eventId}/regenerate-missing-certificates`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.regenerated > 0) {
        toast.success(`${response.data.regenerated} PDF rigenerati con successo!`);
        // Refresh certificates list
        fetchAllCertificates();
      } else {
        toast.info(response.data.message);
      }
      
      // Update certificates status
      fetchEventCertificatesStatus(eventId);
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nella rigenerazione dei PDF';
      toast.error(message);
    } finally {
      setRegeneratingCertificates(null);
    }
  };

  const regenerateAllMissingCertificates = async () => {
    setRegeneratingCertificates('__all__');
    try {
      const response = await axios.post(`${API}/admin/regenerate-missing-certificates`, {});
      const { regenerated = 0, recovered = 0, skipped = 0, total_missing = 0, errors = [] } = response.data || {};
      const fixed = regenerated + recovered;
      if (fixed > 0) {
        toast.success(`Rigenerati ${fixed} attestati su ${total_missing} mancanti${skipped ? ` (saltati: ${skipped})` : ''}`);
      } else if (total_missing === 0) {
        toast.info('Nessun attestato mancante da rigenerare');
      } else {
        toast.warning(`Nessun PDF rigenerato (${total_missing} mancanti, ${skipped} saltati)`);
      }
      if (Array.isArray(errors) && errors.length > 0) {
        console.warn('Regenerazione completata con errori:', errors);
        toast.warning(`${errors.length} errori durante la rigenerazione (vedi console)`);
      }
      fetchAllCertificates();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nella rigenerazione degli attestati mancanti';
      toast.error(message);
    } finally {
      setRegeneratingCertificates(null);
    }
  };

  const fetchEventCertificatesStatus = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/events/${eventId}/certificates-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEventCertificatesStatus(prev => ({
        ...prev,
        [eventId]: response.data
      }));
    } catch (error) {
      console.error('Error fetching certificates status:', error);
    }
  };

  const startEditingEvent = (event) => {
    console.log('Starting to edit event:', event);
    setEditingEvent(event.id);
    setEditPosterFile(null); // Reset file upload
    
    setEditEventData({
      title: event.title || '',
      description: event.description || '',
      type: event.type || 'stage',
      date_tbd: event.date_tbd || false,
      start_date: event.start_date || '',
      end_date: event.end_date || '',
      teacher_ids: Array.isArray(event.teacher_ids) ? [...event.teacher_ids] : [],
      external_teachers_text: event.external_teachers_text || '',
      location: event.location || '',
      max_participants: event.max_participants || 20,
      poster_url: event.poster_url || '',
      dance_analysis: event.dance_analysis || false
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format date range for events (without time)
  const formatDateRange = (startDate, endDate, dateTbd) => {
    if (dateTbd) return 'DATA DA DEFINIRE';
    if (!startDate || !endDate) return 'DATA DA DEFINIRE';
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'DATA DA DEFINIRE';
    
    if (startDate === endDate) {
      return start.toLocaleDateString('it-IT', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric'
      });
    }
    
    const startFormatted = start.toLocaleDateString('it-IT', { 
      day: 'numeric', 
      month: 'long'
    });
    const endFormatted = end.toLocaleDateString('it-IT', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric'
    });
    
    return `${startFormatted} - ${endFormatted}`;
  };

  // Function to get role priority for sorting
  const getRolePriority = (user) => {
    const roles = user.roles || [user.role];
    
    // Check if user is pending approval
    if (!user.approved) return 6; // In attesa always last
    
    // Check role combinations
    const hasAdmin = roles.includes('admin');
    const hasTeacher = roles.includes('teacher');
    const hasFormatore = roles.includes('formatore');
    const hasAtleta = roles.includes('atleta');
    
    if (hasAdmin) return 0;
    if (hasTeacher && hasFormatore) return 1;
    if (hasFormatore) return 2;
    if (hasTeacher) return 3;
    if (hasAtleta) return 4;
    return 5;
  };

  // Function to filter and sort users
  const getFilteredAndSortedUsers = () => {
    let filteredUsers = [...users];
    
    // Apply filter
    if (userFilter !== 'all') {
      if (userFilter === 'pending') {
        filteredUsers = filteredUsers.filter(u => !u.approved);
      } else {
        filteredUsers = filteredUsers.filter(u => {
          const roles = u.roles || [u.role];
          return roles.includes(userFilter) && u.approved;
        });
      }
    }
    
    // Apply sorting
    if (userSortOrder === 'alphabetical') {
      filteredUsers.sort((a, b) => {
        const nameA = `${a.surname} ${a.name}`.toLowerCase();
        const nameB = `${b.surname} ${b.name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } else {
      // Default sorting by role priority
      filteredUsers.sort((a, b) => {
        const priorityA = getRolePriority(a);
        const priorityB = getRolePriority(b);
        if (priorityA !== priorityB) return priorityA - priorityB;
        // Secondary sort by name within same priority
        const nameA = `${a.surname} ${a.name}`.toLowerCase();
        const nameB = `${b.surname} ${b.name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }
    
    return filteredUsers;
  };

  if (!user.approved && user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4" data-testid="approval-pending-message">
        <Card className="max-w-md w-full text-center">
          <CardHeader>
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-orange-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              Account in Attesa
            </CardTitle>
            <CardDescription>
              Il tuo account è in attesa di approvazione da parte dell'amministratore.
              Riceverai una notifica via email quando sarà approvato.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600" data-testid="loading-spinner"></div>
      </div>
    );
  }

  // Build context value for role-specific dashboard components
  const dashboardContextValue = {
    user, activeRole, users, events, getCurrentRole, teachers, formatori,
    setShowProfileModal, isAthleteProfileComplete,
    activeAthleteTab, setActiveAthleteTab,
    mobileMenuOpen, setMobileMenuOpen,
    myEvents, myEnrollments, matchAnalyses, certificates, detailedMatchAnalyses,
    activeTeacherTab, setActiveTeacherTab,
    teacherMobileMenuOpen, setTeacherMobileMenuOpen,
    teacherEvents,
    newMatchAnalysis, setNewMatchAnalysis,
    newDetailedAnalysis, setNewDetailedAnalysis,
    analysisFilters, setAnalysisFilters,
    showCreateDetailedAnalysis, setShowCreateDetailedAnalysis,
    selectedAnalysis, setSelectedAnalysis,
    selectedDetailedAnalysis, setSelectedDetailedAnalysis,
    selectedEnrollmentDetails, setSelectedEnrollmentDetails,
    createMatchAnalysis, createDetailedMatchAnalysis,
    filterMatchAnalyses, clearAnalysisFilters,
    sendAnalysisEmail,
    fetchTeacherEventEnrollments,
    activeFormatoreTab, setActiveFormatoreTab,
    formatoreMobileMenuOpen, setFormatoreMobileMenuOpen,
    fetchFormatoreEventEnrollments,
    setOnlineMeetingLinks, setSelectedEventForLinks,
    activeMaestroTab, setActiveMaestroTab,
    maestroMobileMenuOpen, setMaestroMobileMenuOpen,
    maestroAthletes, maestroEvents,
    selectedAthletesForEnrollment, setSelectedAthletesForEnrollment,
    showEnrollmentModal, setShowEnrollmentModal,
    selectedEventForEnrollment, setSelectedEventForEnrollment,
    showCreateAthleteModal, setShowCreateAthleteModal,
    newAthleteData, setNewAthleteData,
    showDuplicateAthleteDialog, duplicateAthleteInfo,
    showDeletionRequestModal, setShowDeletionRequestModal,
    selectedEnrollmentForDeletion, setSelectedEnrollmentForDeletion,
    deletionReason, setDeletionReason,
    validatePassword, checkDuplicateAthleteAndCreate,
    resetAthleteForm, overwriteAthletePassword,
    enrollAthletesInEvent, requestEnrollmentDeletion,
    selectedMaestroFilter, setSelectedMaestroFilter,
    maestrosForFilter, athletesCreatedByMaestros,
    fetchAthletesCreatedByMaestros, exportMaestroAthletesExcel,
    downloadPaymentPdf, approveMaestroAthleteCreation,
    activeAdminTab, setActiveAdminTab,
    adminMobileMenuOpen, setAdminMobileMenuOpen,
    enrollmentsCount, areaFiltroCount, usersCount,
    getFilteredAndSortedUsers, userFilter, setUserFilter, userSortOrder, setUserSortOrder,
    collapsedRoles, setCollapsedRoles,
    selectedUserId, setSelectedUserId,
    selectedEventEnrollments, setSelectedEventEnrollments,
    newUser, setNewUser, createUser,
    newEvent, setNewEvent, posterFile, setPosterFile, uploadingPoster, createEvent,
    editingEvent, setEditingEvent, editEventData, setEditEventData, editPosterFile, setEditPosterFile,
    pendingEnrollments, pendingPaymentEnrollments,
    allMatchAnalyses, allDetailedMatchAnalyses,
    allCertificates, newCertificate, setNewCertificate,
    certificateFilters, setCertificateFilters, filterCertificates, clearCertificateFilters,
    certificateFile, setCertificateFile, createCertificateWithFile,
    generatingCertificates, regeneratingCertificates,
    searchFilters, setSearchFilters, searchResults, searchUsers, isSearching, clearSearchFilters,
    inconsistentUsers, fixingInconsistent, selectedInconsistentUsers,
    selectAllInconsistentUsers, fixSelectedInconsistentUsers, fixAllInconsistentUsers,
    technicalCreditsModal, setTechnicalCreditsModal,
    approveEnrollment, rejectEnrollment, approveUser, deleteUser,
    confirmPaymentReceived, approveEnrollmentUnpaid, deleteCertificate, deleteMatchAnalysis, deleteDetailedMatchAnalysis,
    deleteEvent, generateEventCertificates, regenerateCertificate, regenerateMissingCertificates, regenerateAllMissingCertificates,
    openPasswordResetModal, startEditingEvent, updateEvent, toggleEventEnrollment,
    toggleInconsistentUserSelection,
    processEnrollmentDeletionRequest, enrollmentDeletionRequests,
    fetchStudyMaterials, setSelectedEventForMaterials,
    formatDate, formatDateRange, fetchUsers,
    incompleteProfiles, sendProfileReminder, sendingReminder, fetchIncompleteProfiles,
    sendPaymentReminder, sendingPaymentReminder,
    sendApprovalReminder, sendingApprovalReminder,
    matchResultForm, setMatchResultForm, adminMatchAnalyses, submitMatchResult,
    rejectedEnrollments, deleteRejectedEnrollment,
    associateAthleteSearch, setAssociateAthleteSearch, associateAthleteResults,
    associateAthlete, transferConflict, setTransferConflict, confirmTransfer,
  };

  return (
    <DashboardContext.Provider value={dashboardContextValue}>
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 py-8">
      {/* Session Warning Dialog */}
      <SessionWarningDialog
        isVisible={showWarning}
        timeRemaining={timeRemaining}
        onRenew={renewSession}
        onLogout={sessionLogout}
      />
      
      {/* Loading Progress Bar */}
      <LoadingProgress 
        progress={loadingProgress} 
        message={loadingMessage} 
        isVisible={isLoadingData && loadingProgress > 0 && loadingProgress < 100} 
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between" data-testid="dashboard-header">
          <div>
            <h1 className="text-3xl font-display font-bold text-gray-900 mb-2">
              Benvenuto/a, {user.name}!
            </h1>
            <p className="text-gray-600">
              Dashboard {getCurrentRole() === 'atleta' ? 'Atleta' : getCurrentRole() === 'teacher' ? 'Insegnante' : getCurrentRole() === 'formatore' ? 'Formatore' : getCurrentRole() === 'maestro' ? 'Maestro' : getCurrentRole() === 'contabile' ? 'Contabile' : 'Amministratore'}
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <PushNotificationToggle />
          </div>
        </div>

        {/* Atleta Dashboard */}
        {getCurrentRole() === 'atleta' && <AtletaDashboard />}

        {/* Teacher Dashboard */}
        {getCurrentRole() === 'teacher' && <TeacherDashboard />}

        {/* Formatore Dashboard */}
        {getCurrentRole() === 'formatore' && <FormatoreDashboard />}

        {/* Maestro Dashboard */}
        {getCurrentRole() === 'maestro' && <MaestroDashboard />}

        {/* Contabile Dashboard */}
        {getCurrentRole() === 'contabile' && <ContabileDashboard />}

        {/* Admin Dashboard */}
        {getCurrentRole() === 'admin' && <AdminDashboard />}
        {/* User Profile Modal */}
        {selectedUserId && (
          <UserProfile
            userId={selectedUserId}
            onClose={() => {
              setSelectedUserId(null);
              // Refresh users data when profile is closed (in case of updates)
              fetchUsers();
            }}
          />
        )}
        
        {/* Event Enrollments Modal */}
        {selectedEventEnrollments && (
          <EventEnrollments
            eventId={selectedEventEnrollments.id}
            eventTitle={selectedEventEnrollments.title}
            onClose={() => setSelectedEventEnrollments(null)}
          />
        )}

        {/* Teacher Event Enrollments Modal */}
        {selectedEventEnrollmentsTeacher && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Atleti Iscritti - {selectedEventEnrollmentsTeacher.title}
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedEventEnrollmentsTeacher(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-blue-800">Evento:</span> {selectedEventEnrollmentsTeacher.title}
                    </div>
                    <div>
                      <span className="font-medium text-blue-800">Data:</span> {formatDateRange(selectedEventEnrollmentsTeacher.start_date, selectedEventEnrollmentsTeacher.end_date, selectedEventEnrollmentsTeacher.date_tbd)}
                    </div>
                    <div>
                      <span className="font-medium text-blue-800">Iscritti:</span> {selectedEventEnrollmentsTeacher.enrollments?.length || 0}/{selectedEventEnrollmentsTeacher.max_participants}
                    </div>
                  </div>
                </div>

                {!selectedEventEnrollmentsTeacher.enrollments || selectedEventEnrollmentsTeacher.enrollments.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    Nessun atleta iscritto a questo evento
                  </p>
                ) : (
                  <div className="space-y-3">
                    {selectedEventEnrollmentsTeacher.enrollments.map((enrollment) => (
                      <div key={enrollment.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex justify-between items-center">
                          <div>
                            <h3 className="font-semibold text-lg">{enrollment.user_name}</h3>
                            <p className="text-sm text-gray-600">
                              Iscritto il: {formatDate(enrollment.enrolled_at)}
                            </p>
                          </div>
                          <div className="text-right">
                            <span 
                              className={`px-3 py-1 rounded-full text-sm font-medium ${
                                enrollment.paid 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {enrollment.paid ? 'Pagato' : 'Non Pagato'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Match Analysis Details Modal */}
        {selectedAnalysis && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">Dettagli Match Analysis</h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedAnalysis(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Evento</Label>
                      <p className="text-lg font-semibold">{selectedAnalysis.event_title}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Atleta</Label>
                      <p className="text-lg font-semibold">{selectedAnalysis.athlete_name}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Insegnante</Label>
                      <p className="text-lg">{selectedAnalysis.teacher_name}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Data Creazione</Label>
                      <p className="text-lg">{formatDate(selectedAnalysis.created_at)}</p>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Contenuto Analisi</Label>
                    <div className="bg-gray-50 rounded-lg p-4 mt-2">
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedAnalysis.content}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detailed Match Analysis Modal */}
        {selectedDetailedAnalysis && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">Dettagli Analisi Dettagliata</h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedDetailedAnalysis(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Evento</Label>
                      <p className="text-lg font-semibold">{selectedDetailedAnalysis.event_title}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Atleta</Label>
                      <p className="text-lg font-semibold">{selectedDetailedAnalysis.athlete_name}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Insegnante</Label>
                      <p className="text-lg">{selectedDetailedAnalysis.teacher_name}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Data Creazione</Label>
                      <p className="text-lg">{formatDate(selectedDetailedAnalysis.created_at)}</p>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Punteggio Totale</Label>
                    <div className="text-3xl font-bold text-blue-600 mb-4">
                      {selectedDetailedAnalysis.total_score?.toFixed(1) || 'N/A'}/10
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-600 mb-3 block">Punteggi Dettagliati</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {Object.entries(selectedDetailedAnalysis.scores || {}).map(([key, value]) => (
                        <div key={key} className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-xs text-gray-600 mb-2">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </div>
                          <div className="text-2xl font-bold text-blue-600">{value}/10</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Note</Label>
                    <div className="bg-gray-50 rounded-lg p-4 mt-2">
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedDetailedAnalysis.notes}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Study Materials Modal */}
        {selectedEventForMaterials && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Materiale Didattico - {selectedEventForMaterials.title}
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedEventForMaterials(null);
                      setStudyMaterials([]);
                      setMaterialUploadForm({ title: '', description: '', file: null });
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                {/* Upload Form for Admin, Teachers, and Formatori */}
                {(getCurrentRole() === 'admin' || getCurrentRole() === 'teacher' || getCurrentRole() === 'formatore') && (
                  <Card className="mb-6">
                    <CardHeader>
                      <CardTitle>Carica Nuovo Materiale</CardTitle>
                      <CardDescription>
                        Condividi PDF, Video o Immagini con gli iscritti {getCurrentRole() === 'formatore' ? 'al corso' : 'all\'evento'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label htmlFor="material-title">Titolo *</Label>
                          <Input
                            id="material-title"
                            value={materialUploadForm.title}
                            onChange={(e) => setMaterialUploadForm(prev => ({...prev, title: e.target.value}))}
                            placeholder="Titolo del materiale"
                          />
                        </div>
                        <div>
                          <Label htmlFor="material-file">File *</Label>
                          <Input
                            id="material-file"
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.mp4,.mpeg,.mov,.avi"
                            onChange={(e) => setMaterialUploadForm(prev => ({...prev, file: e.target.files[0]}))}
                          />
                        </div>
                      </div>
                      <div className="mb-4">
                        <Label htmlFor="material-description">Descrizione</Label>
                        <Textarea
                          id="material-description"
                          value={materialUploadForm.description}
                          onChange={(e) => setMaterialUploadForm(prev => ({...prev, description: e.target.value}))}
                          placeholder="Descrizione del materiale (opzionale)"
                          rows={3}
                        />
                      </div>
                      <Button
                        onClick={() => uploadStudyMaterial(selectedEventForMaterials.id)}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        Carica Materiale
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Materials List */}
                <Card>
                  <CardHeader>
                    <CardTitle>Materiali Disponibili</CardTitle>
                    <CardDescription>
                      Tutto il materiale didattico condiviso per questo evento
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {studyMaterials.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">
                        Nessun materiale didattico disponibile per questo evento
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {studyMaterials.map((material) => (
                          <div key={material.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <h3 className="font-semibold text-lg text-purple-700">
                                  {material.title}
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                  {material.description}
                                </p>
                                <div className="flex items-center justify-between text-xs text-gray-500">
                                  <span>Tipo: {material.file_type}</span>
                                  <span>{(material.file_size / 1024 / 1024).toFixed(2)} MB</span>
                                </div>
                              </div>
                              <div className="ml-2">
                                <div className={`w-3 h-3 rounded-full ${
                                  material.file_type === 'PDF' ? 'bg-red-500' :
                                  material.file_type === 'Video' ? 'bg-blue-500' : 'bg-green-500'
                                }`}></div>
                              </div>
                            </div>
                            
                            <div className="text-xs text-gray-600 mb-3">
                              <p>Caricato da: {material.uploaded_by_name}</p>
                              <p>Data: {formatDate(material.uploaded_at)}</p>
                            </div>

                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 text-purple-600 border-purple-200 hover:bg-purple-50"
                                onClick={() => downloadStudyMaterial(material.id, material.original_filename)}
                              >
                                <Download className="w-3 h-3 mr-1" />
                                Scarica
                              </Button>
                              
                              {getCurrentRole() === 'admin' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => deleteStudyMaterial(material.id)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* Online Meeting Links Modal */}
        {selectedEventForLinks && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Link Incontri Online - {selectedEventForLinks.title}
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedEventForLinks(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Inserisci i link per gli incontri online. Gli iscritti al corso potranno cliccare su questi link per partecipare.
                  </p>

                  <div className="border-b pb-4">
                    <Label htmlFor="link1" className="font-semibold">Incontro Online 1</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                      <div>
                        <Label htmlFor="link1" className="text-sm text-gray-600">Link</Label>
                        <Input
                          id="link1"
                          type="url"
                          value={onlineMeetingLinks.online_meeting_link_1}
                          onChange={(e) => setOnlineMeetingLinks({
                            ...onlineMeetingLinks,
                            online_meeting_link_1: e.target.value
                          })}
                          placeholder="https://meet.google.com/xxx-xxxx-xxx"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="datetime1" className="text-sm text-gray-600">Data e Ora</Label>
                        <Input
                          id="datetime1"
                          type="datetime-local"
                          value={onlineMeetingLinks.online_meeting_datetime_1}
                          onChange={(e) => setOnlineMeetingLinks({
                            ...onlineMeetingLinks,
                            online_meeting_datetime_1: e.target.value
                          })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-b pb-4">
                    <Label htmlFor="link2" className="font-semibold">Incontro Online 2</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                      <div>
                        <Label htmlFor="link2" className="text-sm text-gray-600">Link</Label>
                        <Input
                          id="link2"
                          type="url"
                          value={onlineMeetingLinks.online_meeting_link_2}
                          onChange={(e) => setOnlineMeetingLinks({
                            ...onlineMeetingLinks,
                            online_meeting_link_2: e.target.value
                          })}
                          placeholder="https://meet.google.com/xxx-xxxx-xxx"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="datetime2" className="text-sm text-gray-600">Data e Ora</Label>
                        <Input
                          id="datetime2"
                          type="datetime-local"
                          value={onlineMeetingLinks.online_meeting_datetime_2}
                          onChange={(e) => setOnlineMeetingLinks({
                            ...onlineMeetingLinks,
                            online_meeting_datetime_2: e.target.value
                          })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-b pb-4">
                    <Label htmlFor="link3" className="font-semibold">Incontro Online 3</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                      <div>
                        <Label htmlFor="link3" className="text-sm text-gray-600">Link</Label>
                        <Input
                          id="link3"
                          type="url"
                          value={onlineMeetingLinks.online_meeting_link_3}
                          onChange={(e) => setOnlineMeetingLinks({
                            ...onlineMeetingLinks,
                            online_meeting_link_3: e.target.value
                          })}
                          placeholder="https://meet.google.com/xxx-xxxx-xxx"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="datetime3" className="text-sm text-gray-600">Data e Ora</Label>
                        <Input
                          id="datetime3"
                          type="datetime-local"
                          value={onlineMeetingLinks.online_meeting_datetime_3}
                          onChange={(e) => setOnlineMeetingLinks({
                            ...onlineMeetingLinks,
                            online_meeting_datetime_3: e.target.value
                          })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-b pb-4">
                    <Label htmlFor="link4" className="font-semibold">Incontro Online 4</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                      <div>
                        <Label htmlFor="link4" className="text-sm text-gray-600">Link</Label>
                        <Input
                          id="link4"
                          type="url"
                          value={onlineMeetingLinks.online_meeting_link_4}
                          onChange={(e) => setOnlineMeetingLinks({
                            ...onlineMeetingLinks,
                            online_meeting_link_4: e.target.value
                          })}
                          placeholder="https://meet.google.com/xxx-xxxx-xxx"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="datetime4" className="text-sm text-gray-600">Data e Ora</Label>
                        <Input
                          id="datetime4"
                          type="datetime-local"
                          value={onlineMeetingLinks.online_meeting_datetime_4}
                          onChange={(e) => setOnlineMeetingLinks({
                            ...onlineMeetingLinks,
                            online_meeting_datetime_4: e.target.value
                          })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-2 mt-6">
                    <Button
                      onClick={() => updateOnlineMeetingLinks(selectedEventForLinks.id)}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      Salva Link
                    </Button>
                    <Button
                      onClick={() => setSelectedEventForLinks(null)}
                      variant="outline"
                      className="flex-1"
                    >
                      Annulla
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enrollment Details Modal (for teacher enrollments) */}
        {selectedEnrollmentDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedEnrollmentDetails.title}
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedEnrollmentDetails(null);
                      setEnrollmentStudyMaterials([]);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-6">
                  {/* Event Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-lg mb-2">Informazioni Evento</h3>
                    <div className="space-y-2 text-sm">
                      <p><strong>Data:</strong> {formatDate(selectedEnrollmentDetails.datetime)}</p>
                      <p><strong>Luogo:</strong> {selectedEnrollmentDetails.location || 'Non specificato'}</p>
                      <p><strong>Descrizione:</strong> {selectedEnrollmentDetails.description}</p>
                    </div>
                  </div>

                  {/* Online Meeting Links */}
                  {(selectedEnrollmentDetails.online_meeting_link_1 || 
                    selectedEnrollmentDetails.online_meeting_link_2 || 
                    selectedEnrollmentDetails.online_meeting_link_3 || 
                    selectedEnrollmentDetails.online_meeting_link_4) && (
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold text-lg mb-3 flex items-center">
                        <Link className="w-5 h-5 mr-2 text-purple-600" />
                        Incontri Online
                      </h3>
                      <div className="space-y-3">
                        {selectedEnrollmentDetails.online_meeting_link_1 && (
                          <div className="flex flex-col p-3 bg-purple-50 rounded">
                            <a 
                              href={selectedEnrollmentDetails.online_meeting_link_1} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-purple-600 hover:text-purple-800 font-medium hover:underline flex items-center"
                            >
                              <Link className="w-4 h-4 mr-2" />
                              Incontro Online 1
                            </a>
                            {selectedEnrollmentDetails.online_meeting_datetime_1 && (
                              <span className="text-sm text-gray-600 ml-6 mt-1">
                                <Clock className="w-4 h-4 inline mr-1" />
                                {formatDate(selectedEnrollmentDetails.online_meeting_datetime_1)}
                              </span>
                            )}
                          </div>
                        )}
                        {selectedEnrollmentDetails.online_meeting_link_2 && (
                          <div className="flex flex-col p-3 bg-purple-50 rounded">
                            <a 
                              href={selectedEnrollmentDetails.online_meeting_link_2} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-purple-600 hover:text-purple-800 font-medium hover:underline flex items-center"
                            >
                              <Link className="w-4 h-4 mr-2" />
                              Incontro Online 2
                            </a>
                            {selectedEnrollmentDetails.online_meeting_datetime_2 && (
                              <span className="text-sm text-gray-600 ml-6 mt-1">
                                <Clock className="w-4 h-4 inline mr-1" />
                                {formatDate(selectedEnrollmentDetails.online_meeting_datetime_2)}
                              </span>
                            )}
                          </div>
                        )}
                        {selectedEnrollmentDetails.online_meeting_link_3 && (
                          <div className="flex flex-col p-3 bg-purple-50 rounded">
                            <a 
                              href={selectedEnrollmentDetails.online_meeting_link_3} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-purple-600 hover:text-purple-800 font-medium hover:underline flex items-center"
                            >
                              <Link className="w-4 h-4 mr-2" />
                              Incontro Online 3
                            </a>
                            {selectedEnrollmentDetails.online_meeting_datetime_3 && (
                              <span className="text-sm text-gray-600 ml-6 mt-1">
                                <Clock className="w-4 h-4 inline mr-1" />
                                {formatDate(selectedEnrollmentDetails.online_meeting_datetime_3)}
                              </span>
                            )}
                          </div>
                        )}
                        {selectedEnrollmentDetails.online_meeting_link_4 && (
                          <div className="flex flex-col p-3 bg-purple-50 rounded">
                            <a 
                              href={selectedEnrollmentDetails.online_meeting_link_4} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-purple-600 hover:text-purple-800 font-medium hover:underline flex items-center"
                            >
                              <Link className="w-4 h-4 mr-2" />
                              Incontro Online 4
                            </a>
                            {selectedEnrollmentDetails.online_meeting_datetime_4 && (
                              <span className="text-sm text-gray-600 ml-6 mt-1">
                                <Clock className="w-4 h-4 inline mr-1" />
                                {formatDate(selectedEnrollmentDetails.online_meeting_datetime_4)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Study Materials */}
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-lg mb-3 flex items-center">
                      <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
                      Materiale Didattico
                    </h3>
                    {studyMaterials.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                        <p>Nessun materiale disponibile</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {studyMaterials.map((material) => (
                          <div key={material.id} className="flex items-center justify-between p-3 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                            <div className="flex-1">
                              <h4 className="font-medium">{material.title}</h4>
                              {material.description && (
                                <p className="text-sm text-gray-600">{material.description}</p>
                              )}
                              <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                                <span>{material.file_type?.toUpperCase()}</span>
                                <span>{(material.file_size / 1024).toFixed(2)} KB</span>
                                <span>Caricato il {formatDate(material.uploaded_at)}</span>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadStudyMaterial(material.id, material.original_filename)}
                              data-testid={`enrollment-material-download-${material.id}`}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Scarica
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button
                    onClick={() => {
                      setSelectedEnrollmentDetails(null);
                      setEnrollmentStudyMaterials([]);
                    }}
                    variant="outline"
                  >
                    Chiudi
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Password Reset Modal */}
      {passwordResetModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Reset Password</h2>
              <Button
                onClick={closePasswordResetModal}
                variant="ghost"
                size="sm"
                className="p-1"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Utente:</Label>
                <p className="text-sm text-gray-600 font-medium">{passwordResetModal.userEmail}</p>
              </div>
              
              <div>
                <Label htmlFor="new-password">Nuova Password *</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={passwordResetModal.newPassword}
                  onChange={(e) => setPasswordResetModal({
                    ...passwordResetModal,
                    newPassword: e.target.value
                  })}
                  placeholder="Inserisci la nuova password (min 6 caratteri)"
                  minLength={6}
                  required
                  data-testid="new-password-input"
                />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <Button
                  onClick={closePasswordResetModal}
                  variant="outline"
                  className="flex-1"
                >
                  Annulla
                </Button>
                <Button
                  onClick={resetUserPassword}
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  disabled={passwordResetModal.newPassword.length < 6}
                  data-testid="confirm-password-reset"
                >
                  Reset Password
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      </div>



      {/* Mandatory Maestro School Profile Modal */}
      {showMaestroProfileModal && getCurrentRole() === 'maestro' && user && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60]" data-testid="mandatory-maestro-modal">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-7 h-7 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Completa il Profilo della Scuola</h2>
              <p className="text-sm text-gray-600 mt-1">Inserisci i dati della tua scuola per continuare</p>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold">Nome della Scuola *</Label>
                <Input value={maestroProfileData.school_name} onChange={(e) => setMaestroProfileData({...maestroProfileData, school_name: e.target.value})} placeholder="Es: ASD Danza La Spezia" data-testid="maestro-school-name" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Indirizzo della Scuola *</Label>
                <Input value={maestroProfileData.school_address} onChange={(e) => setMaestroProfileData({...maestroProfileData, school_address: e.target.value})} placeholder="Es: Via Roma 10, 19100 La Spezia" data-testid="maestro-school-address" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-semibold">Telefono di Contatto *</Label>
                  <Input value={maestroProfileData.school_phone} onChange={(e) => setMaestroProfileData({...maestroProfileData, school_phone: e.target.value})} placeholder="+39 ..." data-testid="maestro-school-phone" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Email di Contatto *</Label>
                  <Input type="email" value={maestroProfileData.school_email} onChange={(e) => setMaestroProfileData({...maestroProfileData, school_email: e.target.value})} placeholder="info@scuola.it" data-testid="maestro-school-email" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">P.IVA o CF della Scuola *</Label>
                <Input value={maestroProfileData.school_vat} onChange={(e) => setMaestroProfileData({...maestroProfileData, school_vat: e.target.value.toUpperCase()})} placeholder="Es: 12345678901" data-testid="maestro-school-vat" />
              </div>
            </div>
            <Button onClick={saveMaestroProfile} className="w-full mt-6 bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-maestro-profile-btn">
              Salva e Prosegui
            </Button>
          </div>
        </div>
      )}


      {/* Mandatory Profile Completion Modal - Cannot be dismissed */}
      {showMandatoryProfileModal && getCurrentRole() === 'atleta' && user && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60]" data-testid="mandatory-profile-modal">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-7 h-7 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Completa il tuo Profilo Atleta</h2>
              <p className="text-sm text-gray-500 mt-2">
                Per accedere alla piattaforma, devi compilare tutti i campi obbligatori.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="mandatory-birth-date" className="text-sm font-semibold">Data di Nascita *</Label>
                <Input
                  id="mandatory-birth-date"
                  type="text"
                  placeholder="GG/MM/AAAA"
                  value={mandatoryProfileData.birth_date}
                  onChange={(e) => {
                    let val = e.target.value.replace(/[^\d/]/g, '');
                    // Auto-insert slashes
                    if (val.length === 2 && !val.includes('/')) val += '/';
                    if (val.length === 5 && val.split('/').length === 2) val += '/';
                    if (val.length <= 10) setMandatoryProfileData({...mandatoryProfileData, birth_date: val});
                  }}
                  maxLength={10}
                  className={!mandatoryProfileData.birth_date ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}
                  data-testid="mandatory-birth-date-input"
                />
              </div>

              <div>
                <Label htmlFor="mandatory-fiscal-code" className="text-sm font-semibold">Codice Fiscale *</Label>
                <Input
                  id="mandatory-fiscal-code"
                  type="text"
                  placeholder="RSSMRA85M01H501Z"
                  value={mandatoryProfileData.fiscal_code}
                  onChange={(e) => setMandatoryProfileData({...mandatoryProfileData, fiscal_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')})}
                  maxLength={16}
                  style={{ textTransform: 'uppercase' }}
                  className={!mandatoryProfileData.fiscal_code || mandatoryProfileData.fiscal_code.length !== 16 ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}
                  data-testid="mandatory-fiscal-code-input"
                />
                <p className="text-xs text-gray-500 mt-1">{mandatoryProfileData.fiscal_code.length}/16 caratteri</p>
              </div>

              <div>
                <Label htmlFor="mandatory-athlete-class" className="text-sm font-semibold">Classe di Appartenenza *</Label>
                <Select
                  value={mandatoryProfileData.athlete_class || "placeholder"}
                  onValueChange={(value) => setMandatoryProfileData({...mandatoryProfileData, athlete_class: value === "placeholder" ? "" : value})}
                >
                  <SelectTrigger className={!mandatoryProfileData.athlete_class ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'} data-testid="mandatory-athlete-class-select">
                    <SelectValue placeholder="Seleziona classe" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="placeholder" disabled>Seleziona classe</SelectItem>
                    <SelectItem value="AS">AS</SelectItem>
                    <SelectItem value="A">A</SelectItem>
                    <SelectItem value="B">B</SelectItem>
                    <SelectItem value="C">C</SelectItem>
                    <SelectItem value="D">D</SelectItem>
                    <SelectItem value="E">E</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="mandatory-asa-reference" className="text-sm font-semibold">ASA di Riferimento *</Label>
                <Input
                  id="mandatory-asa-reference"
                  type="text"
                  placeholder="ASA di appartenenza"
                  value={mandatoryProfileData.asa_reference}
                  onChange={(e) => setMandatoryProfileData({...mandatoryProfileData, asa_reference: e.target.value})}
                  className={!mandatoryProfileData.asa_reference ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}
                  data-testid="mandatory-asa-reference-input"
                />
              </div>

              <div>
                <Label htmlFor="mandatory-phone" className="text-sm font-semibold">Telefono *</Label>
                <Input
                  id="mandatory-phone"
                  type="tel"
                  placeholder="+39 333 1234567"
                  value={mandatoryProfileData.phone}
                  onChange={(e) => setMandatoryProfileData({...mandatoryProfileData, phone: e.target.value})}
                  className={!mandatoryProfileData.phone ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}
                  data-testid="mandatory-phone-input"
                />
              </div>

              <div>
                <Label htmlFor="mandatory-address" className="text-sm font-semibold">Indirizzo *</Label>
                <Input
                  id="mandatory-address"
                  type="text"
                  placeholder="Via Roma, 1 - Milano"
                  value={mandatoryProfileData.address}
                  onChange={(e) => setMandatoryProfileData({...mandatoryProfileData, address: e.target.value})}
                  className={!mandatoryProfileData.address ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}
                  data-testid="mandatory-address-input"
                />
              </div>

              <div>
                <Label htmlFor="mandatory-region" className="text-sm font-semibold">Regione *</Label>
                <Select
                  value={mandatoryProfileData.region || "placeholder"}
                  onValueChange={(value) => setMandatoryProfileData({...mandatoryProfileData, region: value === "placeholder" ? "" : value})}
                >
                  <SelectTrigger className={!mandatoryProfileData.region ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'} data-testid="mandatory-region-select">
                    <SelectValue placeholder="Seleziona regione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="placeholder" disabled>Seleziona regione</SelectItem>
                    <SelectItem value="Abruzzo">Abruzzo</SelectItem>
                    <SelectItem value="Basilicata">Basilicata</SelectItem>
                    <SelectItem value="Calabria">Calabria</SelectItem>
                    <SelectItem value="Campania">Campania</SelectItem>
                    <SelectItem value="Emilia-Romagna">Emilia-Romagna</SelectItem>
                    <SelectItem value="Friuli-Venezia Giulia">Friuli-Venezia Giulia</SelectItem>
                    <SelectItem value="Lazio">Lazio</SelectItem>
                    <SelectItem value="Liguria">Liguria</SelectItem>
                    <SelectItem value="Lombardia">Lombardia</SelectItem>
                    <SelectItem value="Marche">Marche</SelectItem>
                    <SelectItem value="Molise">Molise</SelectItem>
                    <SelectItem value="Piemonte">Piemonte</SelectItem>
                    <SelectItem value="Puglia">Puglia</SelectItem>
                    <SelectItem value="Sardegna">Sardegna</SelectItem>
                    <SelectItem value="Sicilia">Sicilia</SelectItem>
                    <SelectItem value="Toscana">Toscana</SelectItem>
                    <SelectItem value="Trentino-Alto Adige">Trentino-Alto Adige</SelectItem>
                    <SelectItem value="Umbria">Umbria</SelectItem>
                    <SelectItem value="Valle d'Aosta">Valle d'Aosta</SelectItem>
                    <SelectItem value="Veneto">Veneto</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Disciplines Section */}
              <div>
                <Label className="text-sm font-semibold">Discipline *</Label>
                <p className="text-xs text-gray-500 mb-2">Seleziona almeno una disciplina o compila il campo "Altro"</p>
                <div className="grid grid-cols-2 gap-2" data-testid="mandatory-disciplines">
                  {['Latin Style', 'Latino Americano (coppia)', 'Danza Classica', 'Danze Jazz', 'Disco Dance', 'Hip Hop', 'Breaking', 'Modern', 'Disco Slow', 'Danze Orientali', 'Tap Dance', 'Show Dance', 'Freestyle', 'Danze Caraibiche'].map((discipline) => {
                    const isSelected = mandatoryProfileData.disciplines.includes(discipline);
                    return (
                      <label
                        key={discipline}
                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors text-sm ${
                          isSelected ? 'border-green-400 bg-green-50 text-green-800' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`discipline-${discipline.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            const newDisciplines = isSelected
                              ? mandatoryProfileData.disciplines.filter(d => d !== discipline)
                              : [...mandatoryProfileData.disciplines, discipline];
                            setMandatoryProfileData({...mandatoryProfileData, disciplines: newDisciplines});
                          }}
                          className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                        {discipline}
                      </label>
                    );
                  })}
                </div>
                <div className="mt-2">
                  <Label htmlFor="mandatory-disciplines-other" className="text-xs font-medium text-gray-600">Altro</Label>
                  <Input
                    id="mandatory-disciplines-other"
                    type="text"
                    placeholder="Altra disciplina..."
                    value={mandatoryProfileData.disciplines_other}
                    onChange={(e) => setMandatoryProfileData({...mandatoryProfileData, disciplines_other: e.target.value})}
                    className={
                      mandatoryProfileData.disciplines.length === 0 && !mandatoryProfileData.disciplines_other
                        ? 'border-red-300 bg-red-50' : ''
                    }
                    data-testid="mandatory-disciplines-other-input"
                  />
                </div>
              </div>
            </div>

            <Button
              onClick={saveMandatoryProfile}
              disabled={savingMandatoryProfile}
              className="w-full mt-6 bg-red-600 hover:bg-red-700 text-white font-semibold py-3"
              data-testid="save-mandatory-profile-btn"
            >
              {savingMandatoryProfile ? 'Salvataggio in corso...' : 'Salva e Prosegui'}
            </Button>

            <p className="text-xs text-center text-gray-400 mt-3">
              Tutti i campi contrassegnati con * sono obbligatori
            </p>
          </div>
        </div>
      )}


      {/* Athlete Profile Modal */}
      {showProfileModal && getCurrentRole() === 'atleta' && user && (
        <UserProfile 
          userId={user.id} 
          isOwnProfile={true}
          onClose={() => {
            setShowProfileModal(false);
            // Reload user data from context to get updated profile
            window.location.reload();
          }} 
        />
      )}

      {/* Email Send Modal */}
      {emailModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Invia Match Analysis via Email</h3>
            <p className="text-gray-600 mb-4">
              Stai per inviare la Match Analysis di <strong>{emailModal.athleteName}</strong>
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Inserisci email del destinatario
              </label>
              <input
                type="email"
                value={emailModal.athleteEmail}
                onChange={(e) => setEmailModal({...emailModal, athleteEmail: e.target.value})}
                placeholder="esempio@email.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setEmailModal({ isOpen: false, analysisId: null, athleteName: '', athleteEmail: '' })}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={confirmSendEmail}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                INVIA
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Technical Credits Modal */}
      {technicalCreditsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Assegna Crediti Tecnici</h3>
            <p className="text-gray-600 mb-4">
              Assegna crediti tecnici a <strong>{technicalCreditsModal.athleteName}</strong>
            </p>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Crediti Tecnici
                </label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={technicalCreditsModal.credits}
                  onChange={(e) => setTechnicalCreditsModal({...technicalCreditsModal, credits: parseInt(e.target.value) || 0})}
                  placeholder="Inserisci numero crediti"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Inserisci il numero di crediti da assegnare (min. 1)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Anno Accademico
                </label>
                <input
                  type="text"
                  value={technicalCreditsModal.academicYear}
                  onChange={(e) => setTechnicalCreditsModal({...technicalCreditsModal, academicYear: e.target.value})}
                  placeholder="2025/2026"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note (opzionale)
                </label>
                <textarea
                  value={technicalCreditsModal.notes}
                  onChange={(e) => setTechnicalCreditsModal({...technicalCreditsModal, notes: e.target.value})}
                  placeholder="Aggiungi note..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setTechnicalCreditsModal({ isOpen: false, athleteId: '', athleteName: '', credits: '', academicYear: '2025/2026', notes: '' })}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={assignTechnicalCredits}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
              >
                ASSEGNA
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enrollment Approval Modal */}
      {approvalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="approval-modal">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Approva Iscrizione</h3>
            <p className="text-gray-600 mb-2">
              Utente: <strong>{approvalModal.enrollment.user_name}</strong>
            </p>
            <p className="text-gray-600 mb-4">
              Evento: <strong>{approvalModal.enrollment.event_title}</strong>
            </p>
            
            {approvalModal.enrollment.selected_dates && approvalModal.enrollment.selected_dates.length > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4" data-testid="approval-selected-dates">
                <p className="text-sm font-semibold text-blue-800 mb-2">
                  Date selezionate dall'atleta: {approvalModal.enrollment.selected_dates.length}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {approvalModal.enrollment.selected_dates.map(d => (
                    <span key={d} className="inline-block px-2 py-1 rounded text-xs bg-blue-100 text-blue-700 font-medium">
                      {new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo di Approvazione
                </label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="approvalType"
                      value="free"
                      checked={approvalModal.type === 'free'}
                      onChange={() => setApprovalModal({...approvalModal, type: 'free', amount: '', amount_cibs: '', amount_midas: ''})}
                      className="w-4 h-4 text-green-600"
                    />
                    <div>
                      <span className="font-medium text-green-700">Gratuita</span>
                      <p className="text-sm text-gray-500">Approva direttamente senza pagamento</p>
                    </div>
                  </label>
                  <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="approvalType"
                      value="paid"
                      checked={approvalModal.type === 'paid'}
                      onChange={() => setApprovalModal({...approvalModal, type: 'paid', amount_cibs: '', amount_midas: ''})}
                      className="w-4 h-4 text-orange-600"
                    />
                    <div>
                      <span className="font-medium text-orange-700">A Pagamento</span>
                      <p className="text-sm text-gray-500">Invia email con richiesta di bonifico CIBS</p>
                    </div>
                  </label>
                  <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="approvalType"
                      value="mixed"
                      checked={approvalModal.type === 'mixed'}
                      onChange={() => setApprovalModal({...approvalModal, type: 'mixed', amount: ''})}
                      className="w-4 h-4 text-blue-600"
                      data-testid="mixed-payment-radio"
                    />
                    <div>
                      <span className="font-medium text-blue-700">Pagamento Misto</span>
                      <p className="text-sm text-gray-500">Invia email con quote separate CIBS e MIDAS</p>
                    </div>
                  </label>
                </div>
              </div>

              {approvalModal.type === 'paid' && (
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <label className="block text-sm font-medium text-orange-700 mb-2">
                    Importo da Pagare (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={approvalModal.amount}
                    onChange={(e) => setApprovalModal({...approvalModal, amount: e.target.value})}
                    placeholder="Es: 50.00"
                    className="w-full px-4 py-2 border border-orange-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    data-testid="payment-amount-input"
                  />
                  <p className="text-xs text-orange-600 mt-2">
                    Verrà inviata un'email all'utente con i dati bancari per il bonifico
                  </p>
                </div>
              )}

              {approvalModal.type === 'mixed' && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
                  <p className="text-xs text-blue-700 bg-blue-100 rounded p-2">
                    Inserisci almeno una delle due quote. Se ne lasci una vuota, sull'email verrà mostrata come <strong>0,00 Gratuito</strong>.
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-red-700 mb-2">
                      Quota CIBS (€) <span className="text-xs text-gray-500 font-normal">(facoltativa)</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={approvalModal.amount_cibs}
                      onChange={(e) => setApprovalModal({...approvalModal, amount_cibs: e.target.value})}
                      placeholder="Lascia vuoto per Gratuito"
                      className="w-full px-4 py-2 border border-red-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      data-testid="mixed-amount-cibs-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Bonifico a COMITATO ITALIANO BALLO SPORTIVO</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-2">
                      Quota MIDAS (€) <span className="text-xs text-gray-500 font-normal">(facoltativa)</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={approvalModal.amount_midas}
                      onChange={(e) => setApprovalModal({...approvalModal, amount_midas: e.target.value})}
                      placeholder="Lascia vuoto per Gratuito"
                      className="w-full px-4 py-2 border border-blue-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid="mixed-amount-midas-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Bonifico a MIDAS SERVIZI SSD A.R.L.</p>
                  </div>
                  {(approvalModal.amount_cibs || approvalModal.amount_midas) && (
                    <div className="pt-3 border-t border-blue-200">
                      <p className="text-sm font-semibold text-gray-800">
                        Totale: € {(parseFloat(approvalModal.amount_cibs || 0) + parseFloat(approvalModal.amount_midas || 0)).toFixed(2).replace('.', ',')}
                      </p>
                    </div>
                  )}
                  <p className="text-xs text-blue-600">
                    Verrà inviata un'email con i dettagli bancari delle quote inserite
                  </p>
                </div>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setApprovalModal(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                data-testid="cancel-approval-btn"
              >
                Annulla
              </button>
              <button
                onClick={handleApprovalConfirm}
                className={`flex-1 px-4 py-2 text-white rounded-md transition-colors font-medium ${
                  approvalModal.type === 'free' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : approvalModal.type === 'mixed'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-orange-600 hover:bg-orange-700'
                }`}
                data-testid="confirm-approval-btn"
              >
                {approvalModal.type === 'free' ? 'APPROVA GRATIS' : approvalModal.type === 'mixed' ? 'INVIA RICHIESTA MISTA' : 'INVIA RICHIESTA'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </DashboardContext.Provider>
  );
};

export default Dashboard;
